import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import JsonLd from './components/JsonLd';
import {
  SITE_URL,
  formatPageTitle,
  formatDescription,
  buildOrganizationSchema,
  buildWebSiteSchema,
  buildSoftwareApplicationSchema,
  buildSchemaGraph,
} from './utils/seo';
import { ChevronDown, ArrowDown, ExternalLink, Play, Menu, X, VolumeX, Scissors, FileText, UploadCloud, Clock, Zap, Layers, Repeat, Heart, TrendingUp, Video, Activity, Shield } from 'react-feather';
import { trackEvent } from './utils/amplitude';
import { getCurrencySymbol } from './utils/currency';
import { AnimatedBeam } from './AnimatedBeam';
import { useDarkMode } from './DarkModeContext';
import api from './api';
import './LandingPage1.css';
import { AiEditorDragShowcase } from './AiEditorDragShowcase';
import TeamMemoji from './TeamMemoji';

const EARLY_ACCESS_LIMIT = 1500;
const APP_DOWNLOAD_URL = 'https://merra.onelink.me/uXXR/0vws5k9w';
const APP_DOWNLOAD_QR_SRC = `${process.env.PUBLIC_URL}/appsflyer-merra-0vws5k9w_qr_code.png`;
const MOBILE_APP_STORE_BADGE = `${process.env.PUBLIC_URL}/badge-app-store.png`;
const MOBILE_GOOGLE_PLAY_BADGE = `${process.env.PUBLIC_URL}/badge-google-play.png`;

const TEAM_MEMBERS = [
    { src: '/minimemoji13.webp', name: 'Boukar' },
    { src: '/minimemoji14.webp', name: 'Marina' },
    { src: '/minimemoji10.webp', name: 'Oleg' },
    { src: '/minimemoji12.webp', name: 'Kim' },
    { src: '/minimemoji9.webp', name: 'Mauricio' },
    { src: '/minimemoji8.webp', name: 'Miguel' },
    { src: '/minimemoji15.webp', name: 'Coralie' },
];

const HERO_SOURCE_CLIPS = [
    { src: `${process.env.PUBLIC_URL}/hero-source-1.webp`, duration: '0:12' },
    { src: `${process.env.PUBLIC_URL}/hero-source-2.png`, duration: '0:08' },
    { src: `${process.env.PUBLIC_URL}/hero-source-3.png`, duration: '0:10' },
];

/** Same breakpoint as `isMobile` (≤768): examples parallax runs above this width */
const PARALLAX_MIN_WIDTH = 769;
/** Testimonials stack at 980px — disable their column parallax at/below that */
const TESTIMONIALS_PARALLAX_MIN_WIDTH = 981;

/** Min width for hero video AI editor overlay (LandingPage1) */
const HERO_AI_OVERLAY_MIN_WIDTH = 1000;

/** 'ios' | 'android' | 'other' — for mobile store badge row */
function getMobileAppStoreKind() {
    if (typeof navigator === 'undefined') return 'other';
    const ua = navigator.userAgent || '';
    if (/iPad|iPhone|iPod/.test(ua)) return 'ios';
    if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) return 'ios';
    if (/Android/i.test(ua)) return 'android';
    return 'other';
}

const scriptOptions = [
    "I don't know who needs to hear this but if you...",
    "Guys, this is wild, I just found out that if you...",
    "In the next 30 seconds, I'll teach you how to..."
];

const voiceoverOptions = [
    "educational tone",
    "casual tone",
    "sarcastic tone"
];

const videoOptions = [
    "fast cut",
    "standard cut",
    "slowmotion"
];

function LandingPage1() {
    const navigate = useNavigate();
    const { darkMode } = useDarkMode();
    const currency = getCurrencySymbol();

    const [showJumbo, setShowJumbo] = useState(true);
    const [jumboOpacity, setJumboOpacity] = useState(1);
    const [isDownloadBannerDismissed, setIsDownloadBannerDismissed] = useState(() => {
        try {
            return localStorage.getItem('downloadBannerDismissed') === 'true';
        } catch {
            return false;
        }
    });
    const [isDesktopQrDismissed, setIsDesktopQrDismissed] = useState(() => {
        try {
            return localStorage.getItem('desktopQrFloatDismissed') === 'true';
        } catch {
            return false;
        }
    });

    const mobileAppStoreKind = useMemo(() => getMobileAppStoreKind(), []);

    useEffect(() => {
        // Arrêter l'animation après 10 secondes
        const timer = setTimeout(() => {
            setShowJumbo(false);
        }, 10000); // 10000ms = 10 secondes

        return () => clearTimeout(timer);
        
    }, []);

    useEffect(() => {
        // Créer un intervalle qui diminue l'opacité toutes les 2 secondes
        const fadeInterval = setInterval(() => {
            setJumboOpacity(prevOpacity => {
                if (prevOpacity <= 0) {
                    clearInterval(fadeInterval); // Arrêter l'intervalle quand l'opacité atteint 0
                    return 0;
                }
                return prevOpacity - 0.2; // Diminuer l'opacité de 0.1
            });
        }, 1000); // Toutes les secondes

        // Nettoyer l'intervalle après 5 étapes (10 secondes total pour le fade)
        setTimeout(() => clearInterval(fadeInterval), 10000);

        return () => clearInterval(fadeInterval);
        
    }, []);

    const [isAuthenticated, setIsAuthenticated] = useState(() => {
        return localStorage.getItem('isAuthenticated') === 'true';
    });
    const [waitlistEmail, setWaitlistEmail] = useState('');
    const [waitlistMessage, setWaitlistMessage] = useState('');
    const [showWaitlistModal, setShowWaitlistModal] = useState(false);
    const [openFAQ, setOpenFAQ] = useState(null);
    const initialWidth = typeof window !== 'undefined' ? window.innerWidth : 1200;
    const [isMobile, setIsMobile] = useState(initialWidth <= 768);
    const [isStackedLayout, setIsStackedLayout] = useState(initialWidth <= 1020);
    const [isHeroVideoPlaying, setIsHeroVideoPlaying] = useState(false);
    const [hasUserClickedPlay, setHasUserClickedPlay] = useState(false);
    const [screenWidth, setScreenWidth] = useState(initialWidth);
    /** Browser mockup: 769–1020px — Step 1 two cols; Step 2 & 3 side by side */
    const isMockupTabletBand = screenWidth > 768 && screenWidth <= 1020;
    const showHeroAiShowcaseOverlay = screenWidth >= HERO_AI_OVERLAY_MIN_WIDTH && !hasUserClickedPlay;
    const [isBurgerMenuOpen, setIsBurgerMenuOpen] = useState(false);
    const [userCount, setUserCount] = useState(null);
    
    // Process section animation state
    const [scriptOptionIndex, setScriptOptionIndex] = useState(0);
    const [voiceoverOptionIndex, setVoiceoverOptionIndex] = useState(0);
    const [videoOptionIndex, setVideoOptionIndex] = useState(0);
    const [activeButtonIndex, setActiveButtonIndex] = useState(null);
    const [buttonOpacity, setButtonOpacity] = useState({ 0: 1, 1: 1, 2: 1 });
    const [shimmerButtonIndex, setShimmerButtonIndex] = useState(null);
    const [mousePointerPosition, setMousePointerPosition] = useState({ x: 0, y: 0, visible: true });
    const currentButtonIndexRef = useRef(0);
    const button0Ref = useRef(null);
    const button1Ref = useRef(null);
    const button2Ref = useRef(null);
    
    // Refs for animated beams
    const inputOutputContainerRef = useRef(null);
    const step1Ref = useRef(null);
    const step2Ref = useRef(null);
    const step3Ref = useRef(null);
    const processRef = useRef(null);
    const outputRef = useRef(null);
    const heroVideoRef = useRef(null);
    const heroVideoContainerRef = useRef(null);
    const heroPipelineRef = useRef(null);
    const heroSource1Ref = useRef(null);
    const heroSource2Ref = useRef(null);
    const heroSource3Ref = useRef(null);
    const heroPipelineLogoRef = useRef(null);
    const heroPipelineOutputRef = useRef(null);
    const heroSourceRefs = [heroSource1Ref, heroSource2Ref, heroSource3Ref];
    const burgerMenuRef = useRef(null);
    const examplesSectionParallaxRef = useRef(null);
    const examplesColLeftRef = useRef(null);
    const examplesColCenterRef = useRef(null);
    const examplesColRightRef = useRef(null);
    const testimonialsSectionParallaxRef = useRef(null);
    const testimonialsColLeftRef = useRef(null);
    const testimonialsColCenterRef = useRef(null);
    const testimonialsColRightRef = useRef(null);
    const [videoScrollProgress, setVideoScrollProgress] = useState(0);
    const [isVideoVisible, setIsVideoVisible] = useState(false);

    const handleHeroVideoPlayClick = useCallback(() => {
        const videoEl = heroVideoRef.current;
        if (!videoEl) return;

        // On mobile, load the video source when user clicks
        if (isMobile && !videoEl.src) {
            videoEl.src = "https://proto-filesharing.s3.eu-north-1.amazonaws.com/public/yo-imagine-desktop.mp4";
            videoEl.load(); // Force load the video
        }

        videoEl.currentTime = 0;
        videoEl.muted = false;
        videoEl.loop = false;
        Promise.resolve(videoEl.play()).catch(() => {});
        setIsHeroVideoPlaying(true);
        setHasUserClickedPlay(true);
    }, [isMobile]);

    // Check screen size for mobile and stacked layout
    useEffect(() => {
        const checkScreenSize = () => {
            const width = window.innerWidth;
            setScreenWidth(width);
            setIsMobile(width <= 768);
            setIsStackedLayout(width <= 1020);
        };
        checkScreenSize();
        window.addEventListener('resize', checkScreenSize);
        return () => window.removeEventListener('resize', checkScreenSize);
    }, []);

    useEffect(() => {
        const sections = Array.from(document.querySelectorAll('.reveal-on-scroll'));
        if (sections.length === 0) return;

        const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
        if (prefersReducedMotion) {
            sections.forEach((el) => el.classList.add('is-visible'));
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) entry.target.classList.add('is-visible');
                });
            },
            /* Delayed reveal (tuned between strict and immediate) */
            { threshold: 0.2, rootMargin: '0px 0px -12% 0px' }
        );

        sections.forEach((el) => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
        if (reduceMotion) return undefined;

        const trios = [
            { section: examplesSectionParallaxRef, left: examplesColLeftRef, center: examplesColCenterRef, right: examplesColRightRef, minWidth: PARALLAX_MIN_WIDTH },
            { section: testimonialsSectionParallaxRef, left: testimonialsColLeftRef, center: testimonialsColCenterRef, right: testimonialsColRightRef, minWidth: TESTIMONIALS_PARALLAX_MIN_WIDTH },
        ];
        const allColumnRefs = trios.flatMap((row) => [row.left, row.center, row.right]);

        const clearTransforms = (refs = allColumnRefs) => {
            refs.forEach((ref) => {
                if (ref.current) ref.current.style.transform = '';
            });
        };

        let rafId = 0;
        const tick = () => {
            const width = window.innerWidth;
            const viewH = window.innerHeight;
            const amp = 42;

            trios.forEach(({ section, left, center, right, minWidth }) => {
                const l = left.current;
                const c = center.current;
                const r = right.current;
                if (!l || !c || !r) return;

                if (width < minWidth) {
                    clearTransforms([left, center, right]);
                    return;
                }

                const s = section.current;
                if (!s) return;

                const rect = s.getBoundingClientRect();
                const focalY = rect.top + rect.height * 0.33;
                const t = Math.max(-1, Math.min(1, (viewH * 0.5 - focalY) / (viewH * 0.55)));
                const ySide = t * -amp;
                const yMid = t * amp;
                l.style.transform = `translate3d(0, ${ySide}px, 0)`;
                c.style.transform = `translate3d(0, ${yMid}px, 0)`;
                r.style.transform = `translate3d(0, ${ySide}px, 0)`;
            });
        };

        const onScrollOrResize = () => {
            if (rafId) return;
            rafId = requestAnimationFrame(() => {
                rafId = 0;
                tick();
            });
        };

        window.addEventListener('scroll', onScrollOrResize, { passive: true });
        window.addEventListener('resize', onScrollOrResize);
        onScrollOrResize();
        return () => {
            window.removeEventListener('scroll', onScrollOrResize);
            window.removeEventListener('resize', onScrollOrResize);
            if (rafId) cancelAnimationFrame(rafId);
            clearTransforms();
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        api.getUserCount()
            .then(({ data }) => { if (!cancelled && data?.count != null) setUserCount(data.count); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        trackEvent('page_viewed', { pageName: 'LandingPage1' });
    }, []);

    const handleDownloadAppClick = useCallback((placement) => {
        trackEvent('download_app_clicked', { page: 'LandingPage1', placement });
        window.open(APP_DOWNLOAD_URL, '_blank', 'noopener,noreferrer');
    }, []);

    const dismissDownloadBanner = useCallback(() => {
        setIsDownloadBannerDismissed(true);
        try {
            localStorage.setItem('downloadBannerDismissed', 'true');
        } catch {}
        trackEvent('download_banner_dismissed', { page: 'LandingPage1' });
    }, []);

    const dismissDesktopQrFloat = useCallback(() => {
        setIsDesktopQrDismissed(true);
        try {
            localStorage.setItem('desktopQrFloatDismissed', 'true');
        } catch {}
        trackEvent('desktop_qr_dismissed', { page: 'LandingPage1' });
    }, []);

    const handleRecapReelClick = () => {
        trackEvent('recap_reel_banner_clicked', { page: 'LandingPage1' });
        navigate('/montage');
    };

    // Close burger menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (burgerMenuRef.current && !burgerMenuRef.current.contains(event.target)) {
                setIsBurgerMenuOpen(false);
            }
        };

        if (isBurgerMenuOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isBurgerMenuOpen]);

    // Parallax scroll effect for video
    useEffect(() => {
        if (!heroVideoContainerRef.current) return;

        const handleScroll = () => {
            if (!heroVideoContainerRef.current) return;
            
            const rect = heroVideoContainerRef.current.getBoundingClientRect();
            const windowHeight = window.innerHeight;
            const elementTop = rect.top;
            const elementHeight = rect.height;
            
            // Calculate progress based on how much of the element is visible
            // This works better with scroll snapping
            const elementBottom = elementTop + elementHeight;
            const viewportTop = 0;
            const viewportBottom = windowHeight;
            
            // Calculate visible portion
            const visibleTop = Math.max(elementTop, viewportTop);
            const visibleBottom = Math.min(elementBottom, viewportBottom);
            const visibleHeight = Math.max(0, visibleBottom - visibleTop);
            
            // On mobile, start revealing at 20% of viewport (so when element is at 80% from top)
            // On desktop, start at 30% of viewport
            const startPoint = windowHeight * (isMobile ? (windowHeight < 768 ? 0.8 : 0.6) : 0.7);
            const endPoint = windowHeight * (isMobile ? (windowHeight < 768 ? 0.2 : 0.4) : 0.3);
            
            let progress = 0;
            if (elementTop < startPoint) {
                // Element has entered the reveal zone
                if (elementTop <= endPoint) {
                    // Fully visible
                    progress = 1;
                } else {
                    // Partially visible - calculate progress
                    const totalDistance = startPoint - endPoint;
                    const currentDistance = elementTop - endPoint;
                    progress = 1 - (currentDistance / totalDistance);
                }
            }
            
            progress = Math.max(0, Math.min(1, progress));
            setVideoScrollProgress(progress);
            setIsVideoVisible(progress > 0.1);
        };

        // Use requestAnimationFrame for smoother updates that work with scroll snapping
        let rafId = null;
        const rafHandleScroll = () => {
            handleScroll();
            rafId = requestAnimationFrame(rafHandleScroll);
        };

        // IntersectionObserver to start/stop the animation loop
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        if (!rafId) {
                            rafId = requestAnimationFrame(rafHandleScroll);
                        }
                    } else {
                        if (rafId) {
                            cancelAnimationFrame(rafId);
                            rafId = null;
                        }
                    }
                });
            },
            {
                threshold: [0, 0.1, 0.5, 1],
                rootMargin: '200px 0px'
            }
        );

        observer.observe(heroVideoContainerRef.current);
        handleScroll(); // Initial check

        return () => {
            observer.disconnect();
            if (rafId) {
                cancelAnimationFrame(rafId);
            }
        };
    }, [isMobile]);

    // Process section button cycling animation
    useEffect(() => {
        const getButtonPosition = (buttonRef) => {
            if (!buttonRef.current) return null;
            const rect = buttonRef.current.getBoundingClientRect();
            const containerRect = processRef.current?.getBoundingClientRect();
            if (!containerRect) return null;
            return {
                x: rect.left + rect.width / 2 - containerRect.left + 20, // 20px right
                y: rect.top + rect.height / 2 - containerRect.top + 20 // 20px lower
            };
        };

        const moveMouseToButton = (buttonIndex, callback) => {
            const buttonRefs = [button0Ref, button1Ref, button2Ref];
            const targetButton = buttonRefs[buttonIndex];
            
            // Small delay to ensure refs are mounted
            setTimeout(() => {
                const position = getButtonPosition(targetButton);
                
                if (position) {
                    // Get current position or start from first button if not visible
                    const currentPos = mousePointerPosition.visible 
                        ? { x: mousePointerPosition.x, y: mousePointerPosition.y }
                        : getButtonPosition(buttonRefs[0]) || { x: 0, y: 0 };
                    
                    // First, set mouse to current position (if not already visible)
                    if (!mousePointerPosition.visible) {
                        setMousePointerPosition({ ...currentPos, visible: true, scale: 1 });
                        // Small delay to ensure initial position is set
                        setTimeout(() => {
                            // Then animate to target button
                            setMousePointerPosition({ ...position, visible: true, scale: 1 });
                            
                            // After movement completes, do click animation
                            setTimeout(() => {
                                setMousePointerPosition(prev => ({ ...prev, scale: 0.8 }));
                                setTimeout(() => {
                                    setMousePointerPosition(prev => ({ ...prev, scale: 1 }));
                                    if (callback) callback();
                                }, 100);
                            }, 300); // Wait for movement transition (0.3s)
                        }, 50);
                    } else {
                        // Mouse is already visible, just move it to new position
                        setMousePointerPosition({ ...position, visible: true, scale: 1 });
                        
                        // After movement completes, do click animation
                        setTimeout(() => {
                            setMousePointerPosition(prev => ({ ...prev, scale: 0.8 }));
                            setTimeout(() => {
                                setMousePointerPosition(prev => ({ ...prev, scale: 1 }));
                                if (callback) callback();
                            }, 100);
                        }, 300); // Wait for movement transition (0.3s)
                    }
                } else {
                    // Fallback: try again or proceed without mouse
                    if (callback) callback();
                }
            }, 50);
        };

        const cycleButtons = () => {
            const currentButtonIndex = currentButtonIndexRef.current;
            
            // Reset opacity for all buttons
            setButtonOpacity({ 0: 1, 1: 1, 2: 1 });
            setShimmerButtonIndex(null);
            
            // Move mouse to current button
            moveMouseToButton(currentButtonIndex, () => {
                // Start shimmer effect after mouse arrives
                setShimmerButtonIndex(currentButtonIndex);
                
                // Animate button click (reduce opacity) - happens after shimmer completes (600ms) + 200ms delay
                setTimeout(() => {
                    setShimmerButtonIndex(null);
                    setActiveButtonIndex(currentButtonIndex);
                    
                    setButtonOpacity(prev => ({
                        ...prev,
                        [currentButtonIndex]: 0.5
                    }));
                    
                    // Reset opacity (button release) after 300ms
                    setTimeout(() => {
                        setButtonOpacity(prev => ({
                            ...prev,
                            [currentButtonIndex]: 1
                        }));
                        setActiveButtonIndex(null);
                    }, 200);
                }, 800); // 600ms shimmer duration + 200ms delay
            });
            
            // Update the corresponding option index after 0.5 seconds (after button is released)
            // Mouse move (300ms) + shimmer (600ms) + delay (200ms) + click animation (300ms) + 500ms after release = 1900ms
            setTimeout(() => {
                if (currentButtonIndex === 0) {
                    setScriptOptionIndex(prev => (prev + 1) % scriptOptions.length);
                } else if (currentButtonIndex === 1) {
                    setVoiceoverOptionIndex(prev => (prev + 1) % voiceoverOptions.length);
                } else if (currentButtonIndex === 2) {
                    setVideoOptionIndex(prev => (prev + 1) % videoOptions.length);
                }
                // Keep mouse visible for next cycle (don't hide it)
            }, 1900); // 300ms mouse move + 600ms shimmer + 200ms delay + 300ms click + 500ms after release
            
            // Move to next button
            currentButtonIndexRef.current = (currentButtonIndex + 1) % 3;
        };
        
        // Start cycling immediately, then every 2 seconds
        cycleButtons();
        const interval = setInterval(cycleButtons, 2000);
        
        return () => clearInterval(interval);
    }, []);

    const handleGoogleSignup = () => {
        trackEvent('button_clicked', { buttonName: 'Google Signup', page: 'LandingPage1' });
        const backendUrl = process.env.NODE_ENV === 'production' 
            ? 'https://proto-backend-d2c1638486b9.herokuapp.com'
            : 'https://proto-backend-d2c1638486b9.herokuapp.com'; //(instead of localhost:5000)
        const returnOrigin = encodeURIComponent(window.location.origin);
        window.location.href = `${backendUrl}/auth/google?return_origin=${returnOrigin}`;
    };

    const handleRequestAccess = () => {
        setShowWaitlistModal(true);
        trackEvent('request_access_clicked', { page: 'LandingPage1' });
    };

    const handleWaitlistSignup = async () => {
        if (!waitlistEmail) {
            setWaitlistMessage('Please enter a valid email.');
            return;
        }
    
        try {
            const response = await fetch('https://proto-backend-d2c1638486b9.herokuapp.com/join-waitlist', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: waitlistEmail }),
            });
    
            if (response.ok) {
                trackEvent('waitlist_signup', { email: waitlistEmail, page: 'LandingPage1' });
                setWaitlistMessage('Thank you for joining the waitlist!');
                setWaitlistEmail('');
                setTimeout(() => {
                    setShowWaitlistModal(false);
                    setWaitlistMessage('');
                }, 2000);
            } else {
                setWaitlistMessage('Failed to join the waitlist. Please try again.');
            }
        } catch (error) {
            console.error('Error joining waitlist:', error);
            setWaitlistMessage('An error occurred. Please try again.');
        }
    };

    const scrollToNext = () => {
        const sections = Array.from(document.querySelectorAll('.snap-section'));
        const viewportHeight = window.innerHeight;
      
        let currentSectionIndex = 0;
        let maxVisibleArea = 0;
      
        sections.forEach((section, i) => {
            const rect = section.getBoundingClientRect();
        
            const visibleTop = Math.max(rect.top, 0);
            const visibleBottom = Math.min(rect.bottom, viewportHeight);
            const visibleArea = Math.max(0, visibleBottom - visibleTop);
      
            if (visibleArea > maxVisibleArea) {
                maxVisibleArea = visibleArea;
                currentSectionIndex = i;
            }
        });
      
        const nextSectionIndex = Math.min(currentSectionIndex + 1, sections.length - 1);
      
        if (nextSectionIndex !== currentSectionIndex) {
            sections[nextSectionIndex].scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            });
        }
    };

    const scrollToExamples = () => {
        const examplesSection = document.getElementById('examples-section');
        if (examplesSection) {
            examplesSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const scrollToTop = () => {
        const firstSection = document.querySelector('.snap-section');
        if (firstSection) {
            firstSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const scrollToPricing = () => {
        const pricingSection = document.getElementById('pricing-section');
        if (pricingSection) {
            pricingSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const scrollToWhatWeDo = () => {
        const whatWeDoSection = document.getElementById('what-we-do-section');
        if (whatWeDoSection) {
            whatWeDoSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const calculateYearProgress = () => {
        const now = new Date();
        const start = new Date(now.getFullYear(), 0, 0); // Début de l'année
        const diff = now - start;
        const oneDay = 1000 * 60 * 60 * 24;
        const dayOfYear = Math.floor(diff / oneDay);
        const progress = (dayOfYear / 365) * 100;
        return progress.toFixed(1); // Retourne le pourcentage avec 1 décimale
    };

    const getDaysRemainingInYear = () => {
        const now = new Date();
        const endOfYear = new Date(now.getFullYear(), 11, 31, 23, 59, 59, 999);
        const diff = endOfYear - now;
        const oneDay = 1000 * 60 * 60 * 24;
        const daysRemaining = Math.ceil(diff / oneDay);
        return daysRemaining;
    };

    const showMobileDownloadBanner = isMobile && !isDownloadBannerDismissed;

    const pageTitle = formatPageTitle('Try Merra AI Free – AI Video Editor & Batch Creation');
    const pageDescription = formatDescription(
        'Start creating social-ready videos with AI voiceover, captions, and batch editing. Try Merra AI free—turn raw clips into publish-ready content in minutes.'
    );
    const landingSchema = buildSchemaGraph([
        buildOrganizationSchema(),
        buildWebSiteSchema(),
        buildSoftwareApplicationSchema({
            name: 'Merra AI',
            description: pageDescription,
            url: `${SITE_URL}/landing-page`,
        }),
    ]);

    return (
        <div className={`landing-page-1${showMobileDownloadBanner ? ' has-download-banner' : ''}`} style={{color: '#111'}}>
            <Helmet>
                <title>{pageTitle}</title>
                <meta name="description" content={pageDescription} />
                <meta name="keywords" content="AI video editor, automatic video editing, voiceover video creator, AI video generator, batch video creation, create multiple videos from one recording, AI video editing for social media, automatic voiceover generator, repurpose video content AI, bulk video creator" />
                <link rel="canonical" href="https://www.merra.ai/landing-page" />
                <meta property="og:title" content="Try Merra AI Free – AI Video Editor & Batch Creation" />
                <meta property="og:description" content={formatDescription('Try Merra AI free: AI video editor with voiceover, captions, and batch creation for social-ready short-form content.')} />
                <meta property="og:url" content="https://www.merra.ai/landing-page" />
            </Helmet>
            <JsonLd schema={landingSchema} />

            {/* Desktop floating QR download widget */}
            {!isMobile && !isDesktopQrDismissed && (
                <div className="download-qr-float">
                    <button
                        type="button"
                        className="download-qr-float__close"
                        onClick={dismissDesktopQrFloat}
                        aria-label="Hide download QR code"
                    >
                        <X size={18} />
                    </button>
                    <a
                        href={APP_DOWNLOAD_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => handleDownloadAppClick('desktop_qr')}
                        className="download-qr-float__card"
                        aria-label="Download Merra AI app"
                    >
                        <img
                            src={APP_DOWNLOAD_QR_SRC}
                            alt="Scan to download Merra AI"
                            className="download-qr-float__img"
                            loading="lazy"
                        />
                        <div className="download-qr-float__text">
                            <div className="download-qr-float__title">Get the App</div>
                            {/* <div className="download-qr-float__subtitle">Scan to get the app</div> */}
                            <div className="download-qr-float__platforms" aria-hidden="true">
                                <span className="download-qr-float__platform" title="iPhone & iPad">
                                    <svg className="download-qr-float__platformIcon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                        <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                                    </svg>
                                    <span className="download-qr-float__platformLabel">iOS</span>
                                </span>
                                <span className="download-qr-float__platform" title="Android">
                                    <svg className="download-qr-float__platformIcon download-qr-float__platformIcon--android" viewBox="0 0 24 24" aria-hidden="true">
                                        <path
                                            fill="#3DDC84"
                                            d="M6 18c0 .55.45 1 1 1h1v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h2v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h1c.55 0 1-.45 1-1V8H6v10zM3.5 8C2.67 8 2 8.67 2 9.5v7c0 .83.67 1.5 1.5 1.5S5 17.33 5 16.5v-7C5 8.67 4.33 8 3.5 8zm17 0c-.83 0-1.5.67-1.5 1.5v7c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5v-7c0-.83-.67-1.5-1.5-1.5zm-4.97-4.55a.5.5 0 00-.5.5c0 .28.22.5.5.5.28 0 .5-.22.5-.5a.5.5 0 00-.5-.5zM8.5 3.5a.5.5 0 00-.5.5c0 .28.22.5.5.5.28 0 .5-.22.5-.5a.5.5 0 00-.5-.5zM12 3c-2.24 0-4.1 1.6-4.5 3.7h9c-.4-2.1-2.26-3.7-4.5-3.7z"
                                        />
                                    </svg>
                                    <span className="download-qr-float__platformLabel">Android</span>
                                </span>
                            </div>
                        </div>
                    </a>
                </div>
            )}

            {/* Header */}
            <header style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '10px 40px',
                backgroundColor: 'rgba(252, 252, 248, 0.60)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                zIndex: 1000,
                // boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px', minWidth: 0 }}>
                    <div 
                        style={{
                            position: 'relative',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            padding: '8px 18px'
                        }}
                        onClick={scrollToTop}
                    >
                        <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            transform: 'translate(-60%, -70%)',
                            width: '300px',
                            height: '50px',
                            background: darkMode 
                                ? 'linear-gradient(90deg, #6495ED, #8A7BC8)'
                                : 'linear-gradient(90deg, #87CEEB, #E6E6FA)',
                            filter: 'blur(15px)',
                            borderRadius: '50%',
                            zIndex: -1,
                            pointerEvents: 'none',
                            opacity: 0.9
                        }} />
                        <span 
                            className="gradient-text" 
                            style={{
                                fontSize: '24px',
                                fontWeight: 700,
                                whiteSpace: 'nowrap',
                                position: 'relative',
                                zIndex: 1
                            }}
                        >
                            Merra.ai
                        </span>
                    </div>

                    {/* {screenWidth > 700 && (
                        <button
                            type="button"
                            onClick={handleRecapReelClick}
                            aria-label="Generate your 2025 recap reel"
                            style={{
                                padding: '9px 14px',
                                borderRadius: '999px',
                                border: '1px solid rgba(92, 75, 211, 0.35)',
                                background: 'linear-gradient(90deg, rgba(45,140,255,0.18), rgba(214,124,255,0.18))',
                                color: '#111',
                                fontSize: '13px',
                                fontWeight: 600,
                                cursor: 'pointer',
                                boxShadow: '0 6px 18px rgba(0,0,0,0.08)',
                                maxWidth: 'min(42vw, 320px)',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0px)';
                            }}
                        >
                            Generate your 2025 recap reel
                        </button>
                    )} */}
                </div>
                <div ref={burgerMenuRef} style={{ display: 'flex', gap: '15px', alignItems: 'center', position: 'relative' }}>
                    {/* What we do - hidden when <= 1000px */}
                    {screenWidth > 700 && (
                        <button
                            onClick={scrollToWhatWeDo}
                            aria-label="Scroll to What we do section"
                            style={{
                                padding: '10px 20px',
                                backgroundColor: 'transparent',
                                border: `0px solid #5C4BD3`,
                                borderRadius: '8px',
                                color: '#5C4BD3',
                                fontSize: '14px',
                                fontWeight: 500,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#F6E7E4';
                                e.target.style.color = 'black';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.backgroundColor = 'transparent';
                                e.target.style.color = '#5C4BD3';
                            }}
                        >
                            What we do
                        </button>
                    )}
                    {/* Pricing - hidden when <= 1200px */}
                    {screenWidth > 900 && (
                        <button
                            onClick={scrollToPricing}
                            aria-label="Scroll to Pricing section"
                            style={{
                                padding: '10px 20px',
                                backgroundColor: 'transparent',
                                border: `0px solid #5C4BD3`,
                                borderRadius: '8px',
                                color: '#5C4BD3',
                                fontSize: '14px',
                                fontWeight: 500,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#F6E7E4';
                                e.target.style.color = 'black';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.backgroundColor = 'transparent';
                                e.target.style.color = '#5C4BD3';
                            }}
                        >
                            Pricing
                        </button>
                    )}
                    {/* Login/Sign up - hidden when <= 800px */}
                    {screenWidth > 500 && (
                        <button
                            onClick={() => {
                                trackEvent('login_signup_clicked', { page: 'LandingPage1' });
                                handleGoogleSignup();
                            }}
                            aria-label="Login or sign up with Google"
                            style={{
                                padding: '9px 20px',
                                backgroundColor: 'transparent',
                                border: `1px solid #5C4BD3`,
                                borderRadius: '0.66em',
                                color: '#5C4BD3',
                                fontSize: '14px',
                                fontWeight: 500,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#D8D8F4';
                                e.target.style.color = 'black';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.backgroundColor = 'transparent';
                                e.target.style.color = '#5C4BD3';
                            }}
                        >
                            Login/Sign up
                        </button>
                    )}
                    {/* Burger Menu - shown when <= 1200px (when first button starts hiding) */}
                    {screenWidth <= 900 && (
                        <>
                            <button
                                onClick={() => setIsBurgerMenuOpen(!isBurgerMenuOpen)}
                                aria-label={isBurgerMenuOpen ? "Close navigation menu" : "Open navigation menu"}
                                aria-expanded={isBurgerMenuOpen}
                                style={{
                                    padding: '8px',
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#5C4BD3'
                                }}
                            >
                                {isBurgerMenuOpen ? <X size={24} /> : <Menu size={24} />}
                            </button>
                            {/* Burger Menu Dropdown - shows only hidden buttons */}
                            {isBurgerMenuOpen && (
                                <div style={{
                                    position: 'absolute',
                                    top: '100%',
                                    right: '0',
                                    marginTop: '10px',
                                    backgroundColor: 'rgba(252, 252, 248, 0.95)',
                                    backdropFilter: 'blur(10px)',
                                    WebkitBackdropFilter: 'blur(10px)',
                                    borderRadius: '8px',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                                    padding: '10px 0',
                                    minWidth: '200px',
                                    zIndex: 1001,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '0'
                                }}>
                                    {/* Recap reel */}
                                    {/* <button
                                        onClick={() => {
                                            handleRecapReelClick();
                                            setIsBurgerMenuOpen(false);
                                        }}
                                        aria-label="Generate your 2025 recap reel"
                                        style={{
                                            padding: '12px 20px',
                                            backgroundColor: 'transparent',
                                            border: 'none',
                                            color: '#111',
                                            fontSize: '14px',
                                            fontWeight: 600,
                                            cursor: 'pointer',
                                            textAlign: 'left',
                                            transition: 'all 0.2s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = '#F6E7E4';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = 'transparent';
                                        }}
                                    >
                                        Generate your 2025 recap reel
                                    </button> */}

                                    {/* Pricing - hidden when <= 1200px */}
                                    {screenWidth <= 900 && (
                                        <button
                                            onClick={() => {
                                                scrollToPricing();
                                                setIsBurgerMenuOpen(false);
                                            }}
                                            aria-label="Scroll to Pricing section"
                                            style={{
                                                padding: '12px 20px',
                                                backgroundColor: 'transparent',
                                                border: 'none',
                                                color: '#5C4BD3',
                                                fontSize: '14px',
                                                fontWeight: 500,
                                                cursor: 'pointer',
                                                textAlign: 'left',
                                                transition: 'all 0.2s ease'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.target.style.backgroundColor = '#F6E7E4';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.target.style.backgroundColor = 'transparent';
                                            }}
                                        >
                                            Pricing
                                        </button>
                                    )}
                                    {/* What we do - hidden when <= 1000px */}
                                    {screenWidth <= 700 && (
                                        <button
                                            onClick={() => {
                                                scrollToWhatWeDo();
                                                setIsBurgerMenuOpen(false);
                                            }}
                                            aria-label="Scroll to What we do section"
                                            style={{
                                                padding: '12px 20px',
                                                backgroundColor: 'transparent',
                                                border: 'none',
                                                color: '#5C4BD3',
                                                fontSize: '14px',
                                                fontWeight: 500,
                                                cursor: 'pointer',
                                                textAlign: 'left',
                                                transition: 'all 0.2s ease'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.target.style.backgroundColor = '#F6E7E4';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.target.style.backgroundColor = 'transparent';
                                            }}
                                        >
                                            What we do
                                        </button>
                                    )}
                                    {/* Login/Sign up - hidden when <= 800px */}
                                    {screenWidth <= 500 && (
                                        <button
                                            onClick={() => {
                                                trackEvent('login_signup_clicked', { page: 'LandingPage1' });
                                                handleGoogleSignup();
                                                setIsBurgerMenuOpen(false);
                                            }}
                                            aria-label="Login or sign up with Google"
                                            style={{
                                                padding: '12px 20px',
                                                backgroundColor: 'transparent',
                                                border: 'none',
                                                color: '#5C4BD3',
                                                fontSize: '14px',
                                                fontWeight: 500,
                                                cursor: 'pointer',
                                                textAlign: 'left',
                                                transition: 'all 0.2s ease'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.target.style.backgroundColor = '#F6E7E4';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.target.style.backgroundColor = 'transparent';
                                            }}
                                        >
                                            Login/Sign up
                                        </button>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </header>

            {/* Mobile download banner */}
            {showMobileDownloadBanner && (
                <div
                    className={`download-banner${mobileAppStoreKind === 'other' ? ' download-banner--dual-stores' : ''}`}
                    role="region"
                    aria-label="Download Merra AI app"
                >
                    <button
                        type="button"
                        className="download-banner__close"
                        onClick={dismissDownloadBanner}
                        aria-label="Dismiss download banner"
                    >
                        <X size={18} />
                    </button>
                    <div className="download-banner__content">
                        <div className="download-banner__title">Download Merra AI</div>
                        <div className="download-banner__subtitle">
                            {mobileAppStoreKind === 'ios' && 'AI video editor — on the App Store'}
                            {mobileAppStoreKind === 'android' && 'AI video editor — on Google Play'}
                            {mobileAppStoreKind === 'other' && 'AI video editor — get the app'}
                        </div>
                    </div>
                    <div className="download-banner__storeWrap">
                        {mobileAppStoreKind === 'ios' && (
                            <a
                                href={APP_DOWNLOAD_URL}
                                className="download-banner__storeLink"
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleDownloadAppClick('mobile_banner_app_store');
                                }}
                            >
                                <img
                                    src={MOBILE_APP_STORE_BADGE}
                                    alt="Download on the App Store"
                                    className="download-banner__storeBadge download-banner__storeBadge--app-store"
                                    width={120}
                                    height={40}
                                />
                            </a>
                        )}
                        {mobileAppStoreKind === 'android' && (
                            <a
                                href={APP_DOWNLOAD_URL}
                                className="download-banner__storeLink"
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleDownloadAppClick('mobile_banner_google_play');
                                }}
                            >
                                <img
                                    src={MOBILE_GOOGLE_PLAY_BADGE}
                                    alt="Get it on Google Play"
                                    className="download-banner__storeBadge download-banner__storeBadge--google-play"
                                    width={135}
                                    height={40}
                                />
                            </a>
                        )}
                        {mobileAppStoreKind === 'other' && (
                            <div className="download-banner__storeBadges">
                                <a
                                    href={APP_DOWNLOAD_URL}
                                    className="download-banner__storeLink"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleDownloadAppClick('mobile_banner_app_store');
                                    }}
                                >
                                    <img
                                        src={MOBILE_APP_STORE_BADGE}
                                        alt="Download on the App Store"
                                        className="download-banner__storeBadge download-banner__storeBadge--app-store"
                                        width={120}
                                        height={40}
                                    />
                                </a>
                                <a
                                    href={APP_DOWNLOAD_URL}
                                    className="download-banner__storeLink"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleDownloadAppClick('mobile_banner_google_play');
                                    }}
                                >
                                    <img
                                        src={MOBILE_GOOGLE_PLAY_BADGE}
                                        alt="Get it on Google Play"
                                        className="download-banner__storeBadge download-banner__storeBadge--google-play"
                                        width={135}
                                        height={40}
                                    />
                                </a>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Hero Section */}
            <section className="snap-section hero-section reveal-on-scroll is-visible" style={{position: 'relative', overflowX: 'hidden'}}>
                <div className="hero-glow" aria-hidden="true" />
                <div className="section-content hero-section-content" style={{zIndex:1, position: 'relative'}}>
                    <div className={`hero-grid${isMobile ? ' hero-grid--mobile' : ''}`}>
                        {/* Left Column - Content */}
                        <div className={`hero-copy${isMobile ? ' hero-copy--mobile' : ''}`}>
                            <div className="hero-eyebrow" role="note">
                                <span className="hero-eyebrow__spark" aria-hidden="true">✦</span>
                                AI VIDEO EDITING FOR BUSY CREATORS AND TEAMS
                            </div>
                            <h1 className="hero-title landing-section-title-solid">
                                Merra <span className="hero-title__accent" aria-label="AI:"><span className="hero-title__accent-a">A</span><span className="hero-title__accent-i">I</span><span className="hero-title__accent-colon">:</span></span> automatic video editing
                            </h1>
                            <p className="hero-subtitle">
                                From raw footage to captivating content in seconds
                            </p>
                            <ul className="hero-feature-row" aria-label="What Merra does">
                                <li className="hero-feature-item">
                                    <span className="hero-feature-item__icon" aria-hidden="true"><Scissors size={22} strokeWidth={2} /></span>
                                    <span className="hero-feature-item__label">Auto-cut<br />raw clips</span>
                                </li>
                                <li className="hero-feature-item">
                                    <span className="hero-feature-item__icon" aria-hidden="true"><FileText size={22} strokeWidth={2} /></span>
                                    <span className="hero-feature-item__label">AI scripts +<br />voiceovers</span>
                                </li>
                                <li className="hero-feature-item">
                                    <span className="hero-feature-item__icon" aria-hidden="true">
                                        <span className="hero-feature-item__cc">CC</span>
                                    </span>
                                    <span className="hero-feature-item__label">Captions,<br />music &amp; more</span>
                                </li>
                                <li className="hero-feature-item">
                                    <span className="hero-feature-item__icon" aria-hidden="true"><UploadCloud size={22} strokeWidth={2} /></span>
                                    <span className="hero-feature-item__label">You post.<br />Done.</span>
                                </li>
                            </ul>
                            <div className={`cta-buttons hero-cta${isMobile ? '' : ' hero-cta--left'}`} style={{width: '100%', marginTop:'10px'}}>
                                <div style={{display:'flex', flexDirection:'column', alignItems: isMobile ? 'center' : 'flex-start', justifyContent:'center'}}>
                                    <p
                                        style={{
                                            wordBreak:'break-word',
                                            color: '#64748B',
                                            margin: 0,
                                            fontSize: '14px',
                                        }}>
                                        Make 3 videos for free • No credit card
                                    </p>
                                </div>
                                <div style={{display:'flex', gap:'15px', alignItems:'center', justifyContent: isMobile ? 'center' : 'flex-start', flexWrap:'wrap'}}>
                                    <button 
                                        onClick={handleGoogleSignup} 
                                        className='google-btn' 
                                        style={{flexShrink:1, maxWidth:'100%'}}
                                        aria-label="Continue with Google to sign up"
                                    >
                                        <div className="google-icon"/>
                                        Continue with Google
                                    </button>
                                </div>
                                <div className="hero-access-pill">
                                    <span className="hero-access-pill__dot" aria-hidden="true" />
                                    <p style={{fontSize:'14px', margin: 0, fontWeight: 500}}>
                                        Limited early access{userCount != null ? `: ${userCount}/${EARLY_ACCESS_LIMIT}` : `: ${EARLY_ACCESS_LIMIT} users`}
                                    </p>
                                </div>
                                <style>{`
                                    @keyframes blink {
                                        0% { opacity: 1; transform: scale(1); }
                                        50% { opacity: 0.4; transform: scale(0.8); }
                                        100% { opacity: 1; transform: scale(1); }
                                    }
                                `}</style>
                            </div>
                        </div>

                        {/* Right Column - Video */}
                        <div 
                            ref={heroVideoContainerRef}
                            className={`hero-visual${!isMobile && !isStackedLayout ? ' hero-visual--pipeline' : ''}`}
                            style={{
                                opacity: isMobile ? videoScrollProgress : 1,
                                transform: isMobile 
                                    ? `translateY(${30 * (1 - videoScrollProgress)}px) scale(${0.95 + 0.05 * videoScrollProgress})`
                                    : 'none',
                                transition: isMobile ? 'opacity 0.3s ease, transform 0.3s ease' : 'none',
                                willChange: isMobile ? 'opacity, transform' : 'auto'
                            }}
                        >
                            <div
                                ref={heroPipelineRef}
                                className={`hero-pipeline${!isMobile && !isStackedLayout ? '' : ' hero-pipeline--video-only'}`}
                            >
                                {!isMobile && !isStackedLayout && (
                                    <>
                                        <AnimatedBeam
                                            containerRef={heroPipelineRef}
                                            fromRef={heroSource1Ref}
                                            toRef={heroPipelineLogoRef}
                                            curvature={36}
                                            duration={4}
                                        />
                                        <AnimatedBeam
                                            containerRef={heroPipelineRef}
                                            fromRef={heroSource2Ref}
                                            toRef={heroPipelineLogoRef}
                                            curvature={0}
                                            duration={4.5}
                                        />
                                        <AnimatedBeam
                                            containerRef={heroPipelineRef}
                                            fromRef={heroSource3Ref}
                                            toRef={heroPipelineLogoRef}
                                            curvature={-36}
                                            duration={5}
                                        />
                                        <AnimatedBeam
                                            containerRef={heroPipelineRef}
                                            fromRef={heroPipelineLogoRef}
                                            toRef={heroPipelineOutputRef}
                                            curvature={-2}
                                            duration={4}
                                        />
                                    </>
                                )}

                                {!isMobile && !isStackedLayout && (
                                    <div className="hero-pipeline__sources" aria-hidden="true">
                                        {HERO_SOURCE_CLIPS.map((clip, index) => (
                                            <div
                                                key={clip.src}
                                                ref={heroSourceRefs[index]}
                                                className="hero-pipeline__clip"
                                            >
                                                <img src={clip.src} alt="" loading="lazy" />
                                                <span className="hero-pipeline__duration">{clip.duration}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {!isMobile && !isStackedLayout && (
                                    <div ref={heroPipelineLogoRef} className="hero-pipeline__logo" aria-hidden="true">
                                        <img
                                            src={`${process.env.PUBLIC_URL}/web-app-manifest-512x512.png`}
                                            alt=""
                                        />
                                    </div>
                                )}

                                <div ref={heroPipelineOutputRef} className="hero-pipeline__output">
                            <div className="hero-video-card">
                                <div className="hero-video-frame">
                                    {/* <span className="hero-ready-badge">
                                        <span className="hero-ready-badge__dot" aria-hidden="true" />
                                        Ready to post
                                    </span> */}
                                    <video
                                        ref={heroVideoRef}
                                        src={(!isMobile || isVideoVisible) ? "https://proto-filesharing.s3.eu-north-1.amazonaws.com/public/yo-imagine-desktop.mp4" : undefined}
                                        controls={hasUserClickedPlay}
                                        preload="metadata"
                                        fetchPriority="high"
                                        loading="lazy"
                                        poster="/poster-mini-yo-light-mini-2.webp"
                                        className="hero-video"
                                        playsInline
                                        muted
                                        loop
                                        autoPlay
                                        onPlay={() => setIsHeroVideoPlaying(true)}
                                        onPause={() => setIsHeroVideoPlaying(false)}
                                    >
                                        {(!isMobile || isVideoVisible) && (
                                            <source src="https://proto-filesharing.s3.eu-north-1.amazonaws.com/public/yo-imagine-desktop.mp4" type="video/mp4" />
                                        )}
                                        Your browser does not support the video tag.
                                    </video>
                                    {/* {showHeroAiShowcaseOverlay && (
                                    <div
                                        style={{
                                            position: 'absolute',
                                            top: '-52px',
                                            left: '-152px',
                                            width: 'calc(100% - 16px)',
                                            maxWidth: '560px',
                                            zIndex: 4,
                                            pointerEvents: 'none',
                                        }}
                                        aria-hidden="true"
                                    >
                                        <AiEditorDragShowcase showPhoneScene={false} />
                                    </div>
                                    )} */}
                                    {!hasUserClickedPlay && (
                                        <button
                                            onClick={handleHeroVideoPlayClick}
                                            aria-label="Play hero video with sound"
                                            className="hero-play-btn"
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.8)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.6)';
                                            }}
                                        >
                                            <Play 
                                                size={40} 
                                                color="white" 
                                                fill="white"
                                                style={{
                                                    marginLeft: '4px'
                                                }}
                                            />
                                        </button>
                                    )}
                                    {!hasUserClickedPlay && (
                                        <button
                                            type="button"
                                            onClick={handleHeroVideoPlayClick}
                                            aria-label="Unmute and play hero video"
                                            className="hero-unmute-btn"
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.8)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.6)';
                                            }}
                                        >
                                            <VolumeX size={18} color="white" />
                                            <span>Unmute</span>
                                        </button>
                                    )}
                                </div>
                                <div className="hero-video-card__footer">
                                    <p className="hero-video-card__title typing-shimmer">
                                        Real human content, AI-edited
                                    </p>
                                    <p className="hero-video-card__meta">
                                        Idea, Script, Voice generation, Editing, Song, Caption
                                    </p>
                                </div>
                            </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div
                        id="hero-value-strip"
                        className="hero-value-strip hero-value-strip--card"
                        aria-label="Key benefits: 10x more from same footage, 2 minutes to publish-ready, zero manual editing, infinite unique variations"
                    >
                        <div className="hero-value-strip__item">
                            <span className="hero-value-strip__icon" aria-hidden="true"><Layers size={18} strokeWidth={2} /></span>
                            <span className="hero-value-strip__text">
                                <strong>10x</strong> more from same footage
                            </span>
                        </div>
                        <div className="hero-value-strip__divider" aria-hidden="true" />
                        <div className="hero-value-strip__item">
                            <span className="hero-value-strip__icon" aria-hidden="true"><Clock size={18} strokeWidth={2} /></span>
                            <span className="hero-value-strip__text">
                                <strong>2 min</strong> to publish-ready
                            </span>
                        </div>
                        <div className="hero-value-strip__divider" aria-hidden="true" />
                        <div className="hero-value-strip__item">
                            <span className="hero-value-strip__icon" aria-hidden="true"><Zap size={18} strokeWidth={2} /></span>
                            <span className="hero-value-strip__text">
                                <strong>0</strong> manual editing
                            </span>
                        </div>
                        <div className="hero-value-strip__divider" aria-hidden="true" />
                        <div className="hero-value-strip__item">
                            <span className="hero-value-strip__icon" aria-hidden="true"><Repeat size={18} strokeWidth={2} /></span>
                            <span className="hero-value-strip__text">
                                <strong>∞</strong> unique variations
                            </span>
                        </div>
                    </div>

                    {/* <button 
                        onClick={scrollToExamples}
                        className="purple-button"
                        style={{ marginTop: '20px' }}
                    >
                        <span>See examples</span>
                        <span className="purple-button-shimmer"></span>
                    </button> */}
                    <div 
                        className="scroll-indicator" 
                        onClick={scrollToNext}
                        role="button"
                        tabIndex={0}
                        aria-label="Scroll to next section"
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                scrollToNext();
                            }
                        }}
                    >
                        <ArrowDown size={32} aria-hidden="true" />
                    </div>
                </div>
            </section>

            {/* Clarification Section */}
            {/* <section className="snap-section">
                <div className="section-content">
                    <p className="section-text">
                        To be fair, it's more like:
                        <br/>
                        <br/>
                        <span style={{fontSize:'24px'}} className="landing-section-title-solid">"Give 10 videos, Merra will make 50"...</span>
                        <br/>
                        <br/>
                        But...
                        <br/>
                        <br/>
                        ...it's easier for you to remember and tell your friend if we say:
                        <br/>
                        <br/>
                        <span style={{fontSize:'24px'}} className="landing-section-title-solid">"Give 5 videos, Merra will make 100"</span>
                        <br/>
                        <br/>
                        
                    </p>
                    <div 
                        className="scroll-indicator" 
                        onClick={scrollToNext}
                        role="button"
                        tabIndex={0}
                        aria-label="Scroll to next section"
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                scrollToNext();
                            }
                        }}
                    >
                        <ArrowDown size={32} aria-hidden="true" />
                    </div>
                </div>
            </section> */}

            {/* Value Proposition Section */}
            {/* <section className="snap-section">
                <div className="section-content">
                    <h2 className="section-title landing-section-title-solid" style={{paddingTop: '100px', fontWeight: 700}}>
                        Merra AI automates your marketing video production
                    </h2>
                    <p className="section-text" style={{marginBottom: '40px'}}>
                        <span style={{fontStyle: 'italic'}}>"It's like delegating our content production an AI team with a voice artist, video producer, editor and content writer." - Our users</span>
                    </p>
                    
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                        gap: '20px',
                        maxWidth: '1000px',
                        margin: '0 auto 40px auto',
                        // width: '100%',
                        justifyContent: 'center',
                        justifySelf: 'center'
                    }}>
                        <div style={{
                            backgroundColor: 'var(--input-bg)',
                            border: '1px solid var(--border-primary)',
                            borderRadius: '12px',
                            padding: '24px',
                            display: 'flex',
                            flexDirection: 'column',
                            marginLeft: isMobile ? '20px' : '0',
                            marginRight: isMobile ? '20px' : '0',
                            gap: '12px',
                            maxWidth: '300px',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.1)', //#5C4BD330' //rgba(0,0,0,0.1) #FD9543, #F996CD #4A9EFF' : '#5C4BD3';
                        }}>
                            <h3 style={{
                                margin: 0,
                                fontSize: '20px',
                                fontWeight: 700,
                                color: 'var(--text-primary)'
                            }}>
                                Save hours and post daily in minutes
                            </h3>
                        </div>
                        
                        <div style={{
                            backgroundColor: 'var(--input-bg)',
                            border: '1px solid var(--border-primary)',
                            borderRadius: '12px',
                            padding: '24px',
                            display: 'flex',
                            flexDirection: 'column',
                            marginLeft: isMobile ? '20px' : '0',
                            marginRight: isMobile ? '20px' : '0',
                            gap: '12px',
                            maxWidth: '300px',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.1)', //#5C4BD330' //rgba(0,0,0,0.1) #FD9543, #F996CD #4A9EFF' : '#5C4BD3';
                        }}>
                            <h3 style={{
                                margin: 0,
                                fontSize: '20px',
                                fontWeight: 700,
                                color: 'var(--text-primary)'
                            }}>
                                Grow with consistent, effortless content
                            </h3>
                        </div>
                        
                        <div style={{
                            backgroundColor: 'var(--input-bg)',
                            border: '1px solid var(--border-primary)',
                            borderRadius: '12px',
                            padding: '24px',
                            display: 'flex',
                            flexDirection: 'column',
                            marginLeft: isMobile ? '20px' : '0',
                            marginRight: isMobile ? '20px' : '0',
                            gap: '12px',
                            maxWidth: '300px',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.1)', //#5C4BD330' //rgba(0,0,0,0.1) #FD9543, #F996CD #4A9EFF' : '#5C4BD3';
                        }}>
                            <h3 style={{
                                margin: 0,
                                fontSize: '20px',
                                fontWeight: 700,
                                color: 'var(--text-primary)'
                            }}>
                                Marketing that feels like storytelling
                            </h3>
                        </div>
                    </div>

                    <div 
                        className="scroll-indicator" 
                        onClick={scrollToNext}
                        role="button"
                        tabIndex={0}
                        aria-label="Scroll to next section"
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                scrollToNext();
                            }
                        }}
                    >
                        <ArrowDown size={32} aria-hidden="true" />
                    </div>
                </div>
            </section> */}

            {/* Not an Editing Tool Section */}
            {/* <section className="snap-section">
                <div className="section-content">
                    <h2 className="section-title landing-section-title-solid" style={{paddingTop: '100px', fontWeight: 700}}>
                        But this is <span style={{textDecoration:'underline black'}}>not an editing tool</span>.
                        <br/>
                        No micromanagement, no overthinking.
                    </h2>
                    <p className="section-text">
                        <br/><strong>It's time to skip :</strong>
                        <br/>
                        <span style={{fontSize:'20px', color:'#333333'}}>
                        ❌ Prompting,
                        <br/>
                        ❌ Coming up with an idea,
                        <br/>
                        ❌ Moving clips on a timeline,
                        <br/>
                        ❌ Trimming milliseconds,
                        <br/>
                        ❌ Picking a song,
                        <br/>
                        ...
                        </span>
                    </p>
                    <div 
                        className="scroll-indicator" 
                        onClick={scrollToNext}
                        role="button"
                        tabIndex={0}
                        aria-label="Scroll to next section"
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                scrollToNext();
                            }
                        }}
                    >
                        <ArrowDown size={32} aria-hidden="true" />
                    </div>
                </div>
            </section> */}

            {/* Time Saving Section */}
            {/* <section className="snap-section">
                <div className="section-content">
                    <h2 className="section-title landing-section-title-solid">
                        As we are all about efficiency and productivity, we want to <span style={{textDecoration:'underline black'}}>save your time now</span>.
                    </h2>
                        <p className="section-text">
                        <strong>So only continue if you say "yes" to all of the questions below.</strong><br />
                        If you say "no" to any of the questions, save yourself the time and<br />
                        <strong>close the tab</strong>.
                    </p>
                    <div 
                        className="scroll-indicator" 
                        onClick={scrollToNext}
                        role="button"
                        tabIndex={0}
                        aria-label="Scroll to next section"
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                scrollToNext();
                            }
                        }}
                    >
                        <ArrowDown size={32} aria-hidden="true" />
                    </div>
                </div>
            </section> */}

            {/* Video Content Section */}
            {/* <section className="snap-section">
                <div className="section-content">
                    <h2 className="section-title landing-section-title-solid">
                        <span style={{textDecoration:'underline black'}}>You have video content</span> on your phone but no time to edit it.
                    </h2>
                    <div 
                        className="scroll-indicator" 
                        onClick={scrollToNext}
                        role="button"
                        tabIndex={0}
                        aria-label="Scroll to next section"
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                scrollToNext();
                            }
                        }}
                    >
                        <ArrowDown size={32} aria-hidden="true" />
                    </div>
                </div>
            </section> */}

            {/* Daily Posting Section */}
            {/* <section className="snap-section">
                <div className="section-content">
                    <h2 className="section-title landing-section-title-solid">
                        <span style={{textDecoration:'underline black'}}>If</span> you had enough content to post multiple times a week to grow your business, <span style={{textDecoration:'underline black'}}>you would do it</span>.
                    </h2>
                    <p className="section-text">
                        (if you think it's too much, it's not for you, close the tab)
                    </p>
                    <div 
                        className="scroll-indicator" 
                        onClick={scrollToNext}
                        role="button"
                        tabIndex={0}
                        aria-label="Scroll to next section"
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                scrollToNext();
                            }
                        }}
                    >
                        <ArrowDown size={32} aria-hidden="true" />
                    </div>
                </div>
            </section> */}

            {/* Voiceover Content Section */}
            {/* <section className="snap-section">
                <div className="section-content">
                    <h2 className="section-title landing-section-title-solid">
                        You want to make <span style={{textDecoration:'underline black'}}>voiceover content</span>.
                    </h2>
                    <p className="section-text">
                        When marketing feels like storytelling.
                        <br/>
                        <br/>
                        (Voiceover = no need to film the narrator = quicker to make content)
                    </p>
                    <div 
                        className="scroll-indicator" 
                        onClick={scrollToNext}
                        role="button"
                        tabIndex={0}
                        aria-label="Scroll to next section"
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                scrollToNext();
                            }
                        }}
                    >
                        <ArrowDown size={32} aria-hidden="true" />
                    </div>
                </div>
            </section> */}

            {/* Speed Over Perfection Section */}
            {/* <section className="snap-section">
                <div className="section-content">
                    <h2 className="section-title landing-section-title-solid">
                        You are not afraid to <span style={{textDecoration:'underline black'}}>post imperfect content</span>
                    </h2>
                    <p className="section-text">
                        (We create content in 2 minutes that makes a Great video.
                        <br/>
                        For PERFECT aesthetic content, human editors are better.)
                    </p>
                    <div 
                        className="scroll-indicator" 
                        onClick={scrollToNext}
                        role="button"
                        tabIndex={0}
                        aria-label="Scroll to next section"
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                scrollToNext();
                            }
                        }}
                    >
                        <ArrowDown size={32} aria-hidden="true" />
                    </div>
                </div>
            </section> */}

            {/* Example Video Section */}
            <section id="what-we-do-section" className="snap-section video-section reveal-on-scroll">
                <div className="section-content">
                    <h2 className="section-title landing-section-title-solid" style={{paddingTop:'100px', fontWeight: 700}}>
                        Easier than writing a tweet.
                    </h2>
                    <p className="section-text" style={{marginBottom: '40px'}}>
                        How it works: Drop 10 random clips, a voice sample, and a description, click generate. Zero editing skills required.
                    </p>
                    <div className="browser-mockup">
                        <div className="browser-mockup-chrome">
                            <div className="browser-dots">
                                <span className="browser-dot browser-dot-red" aria-hidden="true" />
                                <span className="browser-dot browser-dot-yellow" aria-hidden="true" />
                                <span className="browser-dot browser-dot-green" aria-hidden="true" />
                            </div>
                            <div className="browser-url-bar">merra.ai/workspace</div>
                            <div className="browser-fullscreen" aria-hidden="true">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
                                </svg>
                            </div>
                        </div>
                        <div className="browser-mockup-content">
                    <div 
                        ref={inputOutputContainerRef}
                        className={`input-output-container${isMockupTabletBand ? ' mockup-tablet-band' : ''}`}
                        style={{ position: 'relative' }}
                    >
                        {!isMobile && !isStackedLayout && (<>
                        {/* Animated Beams - one from each step to process */}
                        <AnimatedBeam
                            containerRef={inputOutputContainerRef}
                            fromRef={step1Ref}
                            toRef={processRef}
                            curvature={80}
                            duration={4}
                        />
                        <AnimatedBeam
                            containerRef={inputOutputContainerRef}
                            fromRef={step2Ref}
                            toRef={processRef}
                            curvature={40}
                            duration={4.5}
                        />
                        <AnimatedBeam
                            containerRef={inputOutputContainerRef}
                            fromRef={step3Ref}
                            toRef={processRef}
                            curvature={0}
                            duration={5}
                        />
                        {/* Animated Beam - from process to output */}
                        <AnimatedBeam
                            containerRef={inputOutputContainerRef}
                            fromRef={processRef}
                            toRef={outputRef}
                            curvature={0}
                            duration={4}
                        />
                        </>)}
                        
                        {/* Input Steps */}
                        <div className="mockup-step-panel" style={{
                            flex: '1',
                            maxWidth: isMockupTabletBand ? '100%' : '300px',
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '24px',
                            position: 'relative',
                            zIndex: 2
                        }}>
                            <h3
                                className="mockup-step-heading"
                                style={{ textAlign: isMobile ? 'center' : 'left' }}
                            >
                                <span className="mockup-step-title">Step 1</span>
                                <span className="mockup-step-title-suffix">: Upload</span>
                            </h3>
                            <div
                                className={`mockup-step1-cards${isMockupTabletBand ? ' mockup-step1-cards--tablet' : ''}`}
                            >
                            {/* Step 1: Clone your voice */}
                            <div 
                                ref={step1Ref}
                                className="mockup-step1-card"
                                style={{
                                    backgroundColor: 'var(--input-bg)',
                                    border: '1px solid var(--border-primary)',
                                    borderRadius: '12px',
                                    padding: '20px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '12px',
                                    position: 'relative'
                                }}
                            >
                                <p style={{
                                    margin: 0,
                                    fontSize: '18px',
                                    fontWeight: 700,
                                    color: 'var(--text-primary)'
                                }}>
                                    1. Voice sample
                                </p>
                                <audio
                                    controls
                                    style={{
                                        maxWidth: '100%',
                                        height: '40px',
                                        border: '2px solid rgba(0,0,0,0.1)',
                                        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                                        borderRadius: '8px',
                                        padding: '1px 1px',
                                    }}
                                >
                                    <source src="https://proto-filesharing.s3.eu-north-1.amazonaws.com/default-voices-samples/voice_preview_bouki.mp3" type="audio/mpeg" />
                                    Your browser does not support the audio element.
                                </audio>
                            </div>

                            {/* Step 2: Describe your company */}
                            <div 
                                ref={step2Ref}
                                className="mockup-step1-card"
                                style={{
                                    backgroundColor: 'var(--input-bg)',
                                    border: '1px solid var(--border-primary)',
                                    borderRadius: '12px',
                                    padding: '20px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '12px',
                                    position: 'relative'
                                }}
                            >
                                <p style={{
                                    margin: 0,
                                    fontSize: '18px',
                                    fontWeight: 700,
                                    color: 'var(--text-primary)'
                                }}>
                                    2. Company description
                                </p>
                                <div style={{
                                    backgroundColor: 'var(--background-tertiary)',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                                    border: '2px solid rgba(0,0,0,0.1)',
                                    borderRadius: '8px',
                                    padding: '16px',
                                    fontSize: '14px',
                                    color: 'var(--text-secondary)',
                                    lineHeight: '1.6',
                                }}>
                                    "AI short-form content tool"
                                </div>
                            </div>

                            {/* Step 3: Upload your raw content */}
                            <div 
                                ref={step3Ref}
                                className="mockup-step1-card"
                                style={{
                                    backgroundColor: 'var(--input-bg)',
                                    border: '1px solid var(--border-primary)',
                                    borderRadius: '12px',
                                    padding: '20px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '12px',
                                    position: 'relative'
                                }}
                            >
                                <p style={{
                                    margin: 0,
                                    fontSize: '18px',
                                    fontWeight: 700,
                                    color: 'var(--text-primary)'
                                }}>
                                    3. Raw videos
                                </p>
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '8px',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                                    border: '2px solid rgba(0,0,0,0.1)',
                                    borderRadius: '8px',
                                    padding: '16px',
                                }}>
                                    {['Moments', 'Vibing', 'People'].map((folderName, index) => (
                                        <div 
                                            key={index}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '12px',
                                                padding: '16px',
                                                backgroundColor: 'var(--background-tertiary)',
                                                border: '1px solid var(--border-primary)',
                                                borderRadius: '8px'
                                            }}
                                        >
                                            <svg 
                                                width="24" 
                                                height="24" 
                                                viewBox="0 0 24 24" 
                                                fill="none" 
                                                stroke="currentColor" 
                                                strokeWidth="2" 
                                                strokeLinecap="round" 
                                                strokeLinejoin="round"
                                                style={{ color: 'var(--text-secondary)', flexShrink: 0 }}
                                            >
                                                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
                                            </svg>
                                            <span style={{
                                                fontSize: '14px',
                                                color: 'var(--text-secondary)',
                                                fontWeight: '500'
                                            }}>
                                                {folderName}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            </div>
                        </div>

                        {/* Arrow pointing to process */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            padding: '0 20px',
                            opacity: isMobile ? 1 : 0
                        }}>
                            <svg 
                                width="40" 
                                height="40" 
                                viewBox="0 0 24 24" 
                                fill="none" 
                                stroke="currentColor" 
                                strokeWidth="2" 
                                strokeLinecap="round" 
                                strokeLinejoin="round"
                                style={{ color: 'var(--text-primary)' }}
                            >
                                <path d="M5 12h14M12 5l7 7-7 7"/>
                            </svg>
                        </div>

                        {/* Process Section */}
                        <div 
                            ref={processRef}
                            className="mockup-step-panel"
                            style={{
                                flex: '1',
                                maxWidth: isMockupTabletBand ? '100%' : '300px',
                                width: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '16px',
                                position: 'relative',
                                zIndex: 2,
                                justifyContent: 'center',
                                alignSelf: isMockupTabletBand ? 'stretch' : 'center'
                            }}
                        >
                            {/* Animated Mouse Pointer */}
                            {mousePointerPosition.visible && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        left: `${mousePointerPosition.x}px`,
                                        top: `${mousePointerPosition.y}px`,
                                        transform: `translate(-50%, -50%) scale(${mousePointerPosition.scale || 1})`,
                                        transition: mousePointerPosition.scale ? 'transform 0.1s ease' : 'left 0.3s ease, top 0.3s ease',
                                        pointerEvents: 'none',
                                        zIndex: 1000
                                    }}
                                >
                                    <svg
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="red"
                                        stroke="red"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        style={{ filter: 'drop-shadow(0 2px 4px rgba(255,0,0,0.5))' }}
                                    >
                                        <path d="M3 3l7.07 16.97 2.51-7.39 7.39-2.51L3 3z" />
                                    </svg>
                                </div>
                            )}
                            <h3
                                className="mockup-step-heading"
                                style={{ textAlign: isMobile ? 'center' : 'left' }}
                            >
                                <span className="mockup-step-title">Step 2</span>
                                <span className="mockup-step-title-suffix">: Produce</span>
                            </h3>
                            <div style={{
                                backgroundColor: '#f5f5f5',
                                borderRadius: '12px',
                                padding: '20px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '16px',
                                marginTop: 0
                            }}>
                                {/* Script */}
                                <div style={{
                                    backgroundColor: 'var(--input-bg)',
                                    border: '1px solid var(--border-primary)',
                                    borderRadius: '8px',
                                    padding: '16px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '8px'
                                }}>
                                    <h4 style={{
                                        margin: 0,
                                        fontSize: '16px',
                                        fontWeight: 700,
                                        color: 'var(--text-primary)'
                                    }}>
                                        Script
                                    </h4>
                                    <p style={{
                                        margin: 0,
                                        fontSize: '14px',
                                        color: 'var(--text-secondary)',
                                        fontStyle: 'italic'
                                    }}>
                                        "{scriptOptions[scriptOptionIndex]}"
                                    </p>
                                    <button
                                        ref={button0Ref}
                                        aria-label="Regenerate script"
                                        disabled
                                        style={{
                                            padding: '8px 16px',
                                            fontSize: '14px',
                                            backgroundColor: '#e1f679',
                                            border: '1px solid var(--border-primary)',
                                            borderRadius: '6px',
                                            color: 'var(--text-primary)',
                                            cursor: 'default',
                                            opacity: buttonOpacity[0],
                                            transition: 'opacity 0.3s ease',
                                            pointerEvents: 'none',
                                            alignSelf: 'center',
                                            marginTop: '4px'
                                        }}
                                    >
                                        <span className={shimmerButtonIndex === 0 ? 're-generate-shimmer' : ''} style={{ color: 'black' }}>
                                            re-generate
                                        </span>
                                    </button>
                                </div>

                                {/* Voiceover */}
                                <div style={{
                                    backgroundColor: 'var(--input-bg)',
                                    border: '1px solid var(--border-primary)',
                                    borderRadius: '8px',
                                    padding: '16px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '8px'
                                }}>
                                    <h4 style={{
                                        margin: 0,
                                        fontSize: '16px',
                                        fontWeight: 700,
                                        color: 'var(--text-primary)'
                                    }}>
                                        Voiceover
                                    </h4>
                                    <p style={{
                                        margin: 0,
                                        fontSize: '14px',
                                        color: 'var(--text-secondary)',
                                        fontStyle: 'italic'
                                    }}>
                                        "{voiceoverOptions[voiceoverOptionIndex]}"
                                    </p>
                                    <button
                                        ref={button1Ref}
                                        aria-label="Try new voice tone"
                                        disabled
                                        style={{
                                            padding: '8px 16px',
                                            fontSize: '14px',
                                            backgroundColor: '#e1f679',
                                            border: '1px solid var(--border-primary)',
                                            borderRadius: '6px',
                                            color: 'var(--text-primary)',
                                            cursor: 'default',
                                            opacity: buttonOpacity[1],
                                            transition: 'opacity 0.3s ease',
                                            pointerEvents: 'none',
                                            alignSelf: 'center',
                                            marginTop: '4px'
                                        }}
                                    >
                                        <span className={shimmerButtonIndex === 1 ? 're-generate-shimmer' : ''} style={{ color: 'black' }}>
                                            re-generate
                                        </span>
                                    </button>
                                </div>

                                {/* Video */}
                                <div style={{
                                    backgroundColor: 'var(--input-bg)',
                                    border: '1px solid var(--border-primary)',
                                    borderRadius: '8px',
                                    padding: '16px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '8px'
                                }}>
                                    <h4 style={{
                                        margin: 0,
                                        fontSize: '16px',
                                        fontWeight: 700,
                                        color: 'var(--text-primary)'
                                    }}>
                                        Video
                                    </h4>
                                    <p style={{
                                        margin: 0,
                                        fontSize: '14px',
                                        color: 'var(--text-secondary)',
                                        fontStyle: 'italic'
                                    }}>
                                        "{videoOptions[videoOptionIndex]}"
                                    </p>
                                    <button
                                        ref={button2Ref}
                                        aria-label="Regenerate video"
                                        disabled
                                        style={{
                                            padding: '8px 16px',
                                            fontSize: '14px',
                                            backgroundColor: '#e1f679',
                                            border: '1px solid var(--border-primary)',
                                            borderRadius: '6px',
                                            color: 'var(--text-primary)',
                                            cursor: 'default',
                                            opacity: buttonOpacity[2],
                                            transition: 'opacity 0.3s ease',
                                            pointerEvents: 'none',
                                            alignSelf: 'center',
                                            marginTop: '4px'
                                        }}
                                    >
                                        <span className={shimmerButtonIndex === 2 ? 're-generate-shimmer' : ''} style={{ color: 'black' }}>
                                            re-generate
                                        </span>
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Arrow pointing to output */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            padding: '0 20px',
                            opacity: isMobile ? 1 : 0
                        }}>
                            <svg 
                                width="40" 
                                height="40" 
                                viewBox="0 0 24 24" 
                                fill="none" 
                                stroke="currentColor" 
                                strokeWidth="2" 
                                strokeLinecap="round" 
                                strokeLinejoin="round"
                                style={{ color: 'var(--text-primary)' }}
                            >
                                <path d="M5 12h14M12 5l7 7-7 7"/>
                            </svg>
                        </div>

                        {/* Output Video */}
                        <div 
                            ref={outputRef}
                            className="mockup-step-panel"
                            style={{
                                flex: '1',
                                maxWidth: isMockupTabletBand ? '100%' : '300px',
                                width: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '16px',
                                position: 'relative',
                                zIndex: 2,
                            }}
                        >
                            <h3
                                className="mockup-step-heading"
                                style={{ textAlign: isMobile ? 'center' : 'left' }}
                            >
                                <span className="mockup-step-title">Step 3</span>
                                <span className="mockup-step-title-suffix">: Download</span>
                            </h3>
                            <div className="video-container">
                                <video
                                    src="https://proto-filesharing.s3.eu-north-1.amazonaws.com/public/Example-New-GenerateVideoWithVoiceover-Merra-2.mp4"
                                    controls
                                    preload="none"
                                    loading="lazy"
                                    poster="/poster-mini-for-example2.webp"
                                    style={{
                                        maxWidth: '100%',
                                        width: '100%',
                                        maxHeight: isMockupTabletBand ? '420px' : '600px',
                                        minHeight: isMockupTabletBand ? '260px' : '300px',
                                        borderRadius: '12px',
                                        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                                        objectFit: 'contain',
                                    }}
                                    playsInline
                                >
                                    <source src="https://proto-filesharing.s3.eu-north-1.amazonaws.com/public/Example-New-GenerateVideoWithVoiceover-Merra-2.mp4" type="video/mp4" />
                                    Your browser does not support the video tag.
                                </video>
                            </div>
                        </div>
                    </div>
                        </div>
                    </div>
                    <div 
                        className="scroll-indicator" 
                        onClick={scrollToNext} 
                        style={{ marginTop: '40px' }}
                        role="button"
                        tabIndex={0}
                        aria-label="Scroll to next section"
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                scrollToNext();
                            }
                        }}
                    >
                        <ArrowDown size={32} aria-hidden="true" />
                    </div>
                </div>
            </section>

            {/* What You Need to Do Section */}
            {/* <section className="snap-section">
                <div className="section-content">
                    <h2 className="section-title landing-section-title-solid">
                        All you have to do is
                    </h2>
                    <p className="section-text">
                        1) provide your raw content,
                        <br/>
                        <br/>
                        2) describe your business,
                        <br/>
                        <br/>
                        3) clone your voice (optional),
                        <br/>
                        <br/>
                        and regenerate in one click every part that you want to change (script, voiceover, editing...) until you're happy with the result.
                    </p>
                    <div 
                        className="scroll-indicator" 
                        onClick={scrollToNext}
                        role="button"
                        tabIndex={0}
                        aria-label="Scroll to next section"
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                scrollToNext();
                            }
                        }}
                    >
                        <ArrowDown size={32} aria-hidden="true" />
                    </div>
                </div>
            </section> */}

            {/* Examples Section */}
            <section
                id="examples-section"
                ref={examplesSectionParallaxRef}
                className="snap-section reveal-on-scroll"
            >
                <div className="section-content">
                    <h2 className="section-title landing-section-title-solid" style={{paddingTop: '100px', fontWeight: 700}}>Examples</h2>
                    <p className="section-text" style={{marginBottom: '40px'}}>
                        Made with Merra AI
                    </p>
                    <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
                        gap: isMobile ? '40px' : '30px', 
                        maxWidth: '100vw',
                        margin: '0 auto 40px auto'
                    }}>
                        {/* Example Video 1 */}
                        <div
                            ref={examplesColLeftRef}
                            className="landing-parallax-col"
                            style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px',
                        }}
                        >
                            <video
                                src="https://proto-filesharing.s3.eu-north-1.amazonaws.com/public/Kims%20intro%20compressed.mp4"
                                preload="none"
                                loading="lazy"
                                poster="/poster-mini-kim.webp"
                                controls
                                style={{
                                    width: '100%',
                                    maxHeight: '70vh',
                                    objectFit: 'cover',
                                    borderRadius: '12px',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                                }}
                                playsInline
                            >
                                <source src="https://proto-filesharing.s3.eu-north-1.amazonaws.com/public/Kims%20intro%20compressed.mp4" type="video/mp4" />
                                Your browser does not support the video tag.
                            </video>
                            <p style={{
                                margin: '8px 0 0 0',
                                fontSize: '14px',
                                textAlign: 'center',
                                color: '#333',
                                marginBottom: isMobile ? '30px' : '20px'
                            }}>
                                <a href="https://www.instagram.com/keepitmoving.co/" target="_blank" rel="noopener noreferrer" style={{color: '#333', textDecoration: 'none'}}>@keepitmoving.co <span style={{fontSize: '12px', color: darkMode ? '#999' : '#666'}}>(Dance therapy)</span></a>
                            </p>
                        </div>

                        {/* Example Video 2 */}
                        <div
                            ref={examplesColCenterRef}
                            className="landing-parallax-col"
                            style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px',
                        }}
                        >
                            <video
                                src="https://proto-filesharing.s3.eu-north-1.amazonaws.com/public/transpraia%20exhibition%20video.mp4"
                                controls
                                preload="none"
                                loading="lazy"
                                poster="/poster-mini-transpraia.webp"
                                style={{
                                    width: '100%',
                                    maxHeight: '70vh',
                                    objectFit: 'cover',
                                    borderRadius: '12px',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                                }}
                                playsInline
                            >
                                <source src="https://proto-filesharing.s3.eu-north-1.amazonaws.com/public/transpraia%20exhibition%20video.mp4" type="video/mp4" />
                                Your browser does not support the video tag.
                            </video>
                            <p style={{
                                margin: '8px 0 0 0',
                                fontSize: '14px',
                                textAlign: 'center',
                                color: '#333',
                                marginBottom: isMobile ? '30px' : '20px'
                            }}>
                                <a href="https://www.instagram.com/p/DOgWmwhDCtt/" target="_blank" rel="noopener noreferrer" style={{color: '#333', textDecoration: 'none'}}>@transpraia <span style={{fontSize: '12px', color: darkMode ? '#999' : '#666'}}>(Art exhibition)</span></a>
                            </p>
                        </div>
                        {/* Example Video 3 */}
                        <div
                            ref={examplesColRightRef}
                            className="landing-parallax-col"
                            style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px',
                        }}
                        >
                            <video
                                src="https://proto-filesharing.s3.eu-north-1.amazonaws.com/public/Amber%20intro.mp4"
                                controls
                                preload="none"
                                loading="lazy"
                                poster="/poster-mini-amber.webp"
                                style={{
                                    width:'100%',
                                    maxHeight:'70vh',
                                    objectFit:'cover',
                                    borderRadius:'12px',
                                    boxShadow:'0 4px 20px rgba(0,0,0,0.1)',
                                }}
                                playsInline
                            >
                                <source src="https://proto-filesharing.s3.eu-north-1.amazonaws.com/public/Amber%20intro.mp4" type="video/mp4" />
                                Your browser does not support the video tag.
                            </video>
                            <p style={{
                                margin: '8px 0 0 0',
                                fontSize: '14px',
                                textAlign: 'center',
                                color: '#333',
                                marginBottom: isMobile ? '30px' : '20px'
                            }}>
                                <a href="https://www.instagram.com/notonthe_feed/" target="_blank" rel="noopener noreferrer" style={{color: '#333', textDecoration: 'none'}}>@notOnThe_Feed <span style={{fontSize: '12px', color: darkMode ? '#999' : '#666'}}>(Media)</span></a>
                            </p>
                        </div>
                    </div>

                    {/* <button 
                        onClick={scrollToTop}
                        className="purple-button"
                        style={{ marginTop: '20px' }}
                    >
                        <span>Back to top</span>
                        <span className="purple-button-shimmer"></span>
                    </button> */}

                    <div 
                        className="scroll-indicator" 
                        onClick={scrollToNext} 
                        style={{ marginTop: '40px' }}
                        role="button"
                        tabIndex={0}
                        aria-label="Scroll to next section"
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                scrollToNext();
                            }
                        }}
                    >
                        <ArrowDown size={32} aria-hidden="true" />
                    </div>
                </div>
            </section>

            {/* For Who Section */}
            <section className="snap-section for-who-section reveal-on-scroll" style={{position: 'relative', overflowX: 'hidden'}}>
                <div className="for-who-glow" aria-hidden="true" />
                <div className="section-content" style={{zIndex: 1, position: 'relative'}}>
                    <h2 className="section-title landing-section-title-solid" style={{paddingTop: '100px', fontWeight: 700}}>
                        For who ?
                    </h2>
                    <p className="section-text" style={{marginBottom: '40px'}}>
                        For creators, companies and agencies.
                    </p>

                    {/* Tables */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                        gap: '30px',
                        maxWidth: '1000px',
                        margin: '0 auto',
                        width: '100%'
                    }}>
                        {/* Merra is for you table */}
                        <div className="for-who-card" style={{
                            backgroundColor: 'var(--input-bg)',
                            border: '1px solid var(--border-primary)',
                            borderRadius: '12px',
                            overflow: 'hidden',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                            marginLeft: isMobile ? '20px' : '0',
                            marginRight: isMobile ? '20px' : '0',
                        }}>
                            <div style={{
                                padding: 'Opx',
                                paddingBottom: '10px',
                                paddingTop: '20px',
                                backgroundColor: 'var(--background-tertiary)',
                                borderBottom: '1px solid var(--border-primary)',
                                fontWeight: 'bold',
                                fontSize: '22px',
                                color: darkMode ? '#4CAF50' : '#4CAF50',
                                margin: '0 auto',
                            }} className="typing-shimmer">
                                Merra AI is for you if
                            </div>
                            {/* <div style={{
                                backgroundColor: 'var(--background-tertiary)',
                                borderBottom: '1px solid var(--border-primary)',
                                fontWeight: 'bold',
                                fontSize: '18px',
                                color: 'var(--text-primary)',
                                paddingBottom: '20px',
                            }}>
                                if this sounds like you
                            </div> */}
                            <div style={{padding: '0', display: 'grid', gridTemplateColumns: '1fr auto'}}>
                                <div style={{
                                    padding: '16px 20px',
                                    borderBottom: '1px solid var(--border-primary)',
                                    borderRight: '1px solid var(--border-primary)',
                                    color: 'var(--text-primary)'
                                }}>
                                    You want to post consistently without editing for hours
                                </div>
                                <div style={{
                                    padding: '16px 20px',
                                    borderBottom: '1px solid var(--border-primary)',
                                    color: '#4CAF50',
                                    fontSize: '18px',
                                    fontWeight: 'bold',
                                    textAlign: 'center',
                                    minWidth: '80px'
                                }}>
                                    ✅ Yes
                                </div>
                                <div style={{
                                    padding: '16px 20px',
                                    borderBottom: '1px solid var(--border-primary)',
                                    borderRight: '1px solid var(--border-primary)',
                                    color: 'var(--text-primary)'
                                }}>
                                    You have clips on your phone but never turn them into content
                                </div>
                                <div style={{
                                    padding: '16px 20px',
                                    borderBottom: '1px solid var(--border-primary)',
                                    color: '#4CAF50',
                                    fontSize: '18px',
                                    fontWeight: 'bold',
                                    textAlign: 'center',
                                    minWidth: '80px'
                                }}>
                                    ✅ Yes
                                </div>
                                <div style={{
                                    padding: '16px 20px',
                                    borderBottom: '1px solid var(--border-primary)',
                                    borderRight: '1px solid var(--border-primary)',
                                    color: 'var(--text-primary)'
                                }}>
                                    You want storytelling
                                </div>
                                <div style={{
                                    padding: '16px 20px',
                                    borderBottom: '1px solid var(--border-primary)',
                                    color: '#4CAF50',
                                    fontSize: '18px',
                                    fontWeight: 'bold',
                                    textAlign: 'center',
                                    minWidth: '80px'
                                }}>
                                    ✅ Yes
                                </div>
                            </div>
                        </div>

                        {/* Merra is not for you table */}
                        <div className="for-who-card" style={{
                            backgroundColor: 'var(--input-bg)',
                            border: '1px solid var(--border-primary)',
                            borderRadius: '12px',
                            overflow: 'hidden',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                            marginLeft: isMobile ? '20px' : '0',
                            marginRight: isMobile ? '20px' : '0',
                        }}>
                            <div style={{
                                padding: 'Opx',
                                paddingBottom: '10px',
                                paddingTop: '20px',
                                backgroundColor: 'var(--background-tertiary)',
                                borderBottom: '1px solid var(--border-primary)',
                                fontWeight: 'bold',
                                fontSize: '22px',
                                color: '#ab5898',
                                margin: '0 auto',
                            }}>
                                Merra AI is NOT for you if
                            </div>
                            {/* <div style={{
                                backgroundColor: 'var(--background-tertiary)',
                                borderBottom: '1px solid var(--border-primary)',
                                fontWeight: 'bold',
                                fontSize: '18px',
                                color: 'var(--text-primary)',
                                paddingBottom: '20px',
                            }}>
                                if this sounds like you
                            </div> */}
                            <div style={{padding: '0', display: 'grid', gridTemplateColumns: '1fr auto'}}>
                                <div style={{
                                    padding: '16px 20px',
                                    borderBottom: '1px solid var(--border-primary)',
                                    borderRight: '1px solid var(--border-primary)',
                                    color: 'var(--text-primary)'
                                }}>
                                    You want cinematic editing with complex transitions, effects and animations
                                </div>
                                <div style={{
                                    padding: '16px 20px',
                                    borderBottom: '1px solid var(--border-primary)',
                                    color: '#dc3545',
                                    fontSize: '18px',
                                    fontWeight: 'bold',
                                    textAlign: 'center',
                                    minWidth: '80px'
                                }}>
                                    ❌ No
                                </div>
                                <div style={{
                                    padding: '16px 20px',
                                    borderBottom: '1px solid var(--border-primary)',
                                    borderRight: '1px solid var(--border-primary)',
                                    color: 'var(--text-primary)'
                                }}>
                                    You need to repurpose long videos into shorts
                                </div>
                                <div style={{
                                    padding: '16px 20px',
                                    borderBottom: '1px solid var(--border-primary)',
                                    color: '#dc3545',
                                    fontSize: '18px',
                                    fontWeight: 'bold',
                                    textAlign: 'center',
                                    minWidth: '80px'
                                }}>
                                    ❌ No
                                </div>
                                <div style={{
                                    padding: '16px 20px',
                                    borderRight: '1px solid var(--border-primary)',
                                    color: 'var(--text-primary)'
                                }}>
                                    You want full control of every frame on a timeline
                                </div>
                                <div style={{
                                    padding: '16px 20px',
                                    color: '#dc3545',
                                    fontSize: '18px',
                                    fontWeight: 'bold',
                                    textAlign: 'center',
                                    minWidth: '80px'
                                }}>
                                    ❌ No
                                </div>
                            </div>
                        </div>
                    </div>

                    <div 
                        className="scroll-indicator" 
                        onClick={scrollToNext} 
                        style={{marginTop: '40px'}}
                        role="button"
                        tabIndex={0}
                        aria-label="Scroll to next section"
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                scrollToNext();
                            }
                        }}
                    >
                        <ArrowDown size={32} aria-hidden="true" />
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="snap-section cta-section reveal-on-scroll">
                <div className="section-content">
                    <h2 className="section-title landing-section-title-solid">
                        Ready to create?
                    </h2>
                    <p className="section-text">
                        Get your first videos for free, in 2 minutes.
                        <br/> <span style={{opacity: 0.6, fontStyle: 'italic'}}>Let's see if you can 10x your reach in 30 days.</span>
                    </p>
                    <div style={{
                        padding: '20px',
                        backgroundColor: 'var(--background-tertiary)',
                        borderBottom: '1px solid var(--border-primary)',
                        fontWeight: 'bold',
                        fontSize: '16px',
                        color: darkMode ? '#ff6b6b' : '#dc3545'
                    }}>
                        Limited early access{userCount != null ? `: ${userCount}/${EARLY_ACCESS_LIMIT}` : `: ${EARLY_ACCESS_LIMIT} users`}
                    </div>
                    <div className="cta-buttons">
                        {/* <p style={{color: 'black'}}>
                            Use your google account to sign up
                            <br/>
                            <br/>
                            No credit card required.
                        </p> */}
                        <div style={{display:'flex', gap:'15px', alignItems:'center', justifyContent:'center', flexWrap:'wrap'}}>
                            <button 
                                onClick={handleGoogleSignup} 
                                className='google-btn' 
                                style={{flexShrink:1, maxWidth:'100%'}}
                                aria-label="Continue with Google to sign up"
                            >
                                <div className="google-icon"/>
                                Continue with Google
                            </button>
                        </div>
                        <div style={{display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center'}}>
                            <p
                                style={{
                                    wordBreak:'break-word',
                                    color: '#333', 
                                    margin: 0,
                                }}>
                                Make 3 videos for free • no credit card
                            </p>
                        </div>
                    </div>
                    {/* <div className="cta-buttons">
                        {isAuthenticated ? (
                            <button 
                                onClick={handleGoogleSignup} 
                                className="google-btn"
                                aria-label="Continue with Google to sign up"
                            >
                                <div className="google-icon" />
                                Continue with Google
                            </button>
                        ) : (
                            <>
                                <button 
                                    onClick={handleGoogleSignup} 
                                    className="google-btn"
                                    aria-label="Continue with Google to sign up"
                                >
                                    <div className="google-icon" />
                                    Continue with Google
                                </button>
                                <button onClick={handleRequestAccess} className="purple-button">
                                    <span>Request access</span>
                                    <span className="purple-button-shimmer"></span>
                                </button>
                                <button 
                                    onClick={() => window.open('https://calendly.com/boukar-o-sall/30min', '_blank')}
                                    className="purple-button"
                                    style={{ marginTop: '12px' }}
                                >
                                    <span>Book a 30-minute call with the founder</span>
                                    <span className="purple-button-shimmer"></span>
                                </button>
                            </>
                        )}
                    </div> */}
                    <div 
                        className="scroll-indicator" 
                        onClick={scrollToNext} 
                        style={{ marginTop: '40px' }}
                        role="button"
                        tabIndex={0}
                        aria-label="Scroll to next section"
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                scrollToNext();
                            }
                        }}
                    >
                        <ArrowDown size={32} aria-hidden="true" />
                    </div>
                </div>
            </section>

            {/* Testimonials Section */}
            <section
                id="testimonials-section"
                ref={testimonialsSectionParallaxRef}
                className="snap-section testimonials-section reveal-on-scroll"
            >
                <div className="section-content testimonials-section__content">
                    <p className="testimonials-section__label">Testimonials</p>
                    <h2 className="testimonials-section__title landing-section-title-solid">
                        Loved by creators and businesses who post to grow.
                    </h2>
                    <p className="testimonials-section__subtitle">
                        Merra turns raw footage into content that gets views, saves time, and grows brands.
                    </p>

                    <div className="testimonials-grid">
                        {/* Testimonial 1 */}
                        <article
                            ref={testimonialsColLeftRef}
                            className="t-card landing-parallax-col"
                        >
                            <div className="t-card__quote-icon" aria-hidden="true">“</div>
                            <p className="t-card__quote">
                                “I&apos;ve stopped editing my own videos.
                                It sounds like a clickbait, but I just upload raw clips to Merra and it gives me scripts, hooks, voiceovers and finished videos.
                                Ngl, it feels like cheating.”
                            </p>
                            <div className="t-card__stars" aria-label="5 out of 5 stars">
                                {[0, 1, 2, 3, 4].map((i) => (
                                    <span key={i} className="t-card__star" aria-hidden="true">★</span>
                                ))}
                            </div>
                            <div className="t-card__divider" />
                            <div className="t-card__stats">
                                <div className="t-card__stat">
                                    <span className="t-card__stat-icon" aria-hidden="true"><TrendingUp size={16} strokeWidth={2.25} /></span>
                                    <span className="t-card__stat-copy">
                                        <strong>2x</strong>
                                        <span>more views</span>
                                    </span>
                                </div>
                                <div className="t-card__stat">
                                    <span className="t-card__stat-icon" aria-hidden="true"><Heart size={16} strokeWidth={2.25} /></span>
                                    <span className="t-card__stat-copy">
                                        <strong>3x</strong>
                                        <span>higher engagement</span>
                                    </span>
                                </div>
                            </div>
                            <div className="t-card__author">
                                <span className="t-card__avatar t-card__avatar--sarah" aria-hidden="true">AM</span>
                                <span className="t-card__author-meta">
                                    <span className="t-card__author-name">Anya M.</span>
                                    <span className="t-card__author-role">Creator</span>
                                </span>
                            </div>
                        </article>

                        {/* Testimonial 2 */}
                        <article
                            ref={testimonialsColCenterRef}
                            className="t-card landing-parallax-col"
                        >
                            <div className="t-card__quote-icon" aria-hidden="true">“</div>
                            <p className="t-card__quote">
                                “Magnificent service. Because nobody has time to spend 2 hours on a Reel, let&apos;s stop normalize that. So now I have a 2min slot in my agenda to use Merra, and I post daily.”
                            </p>
                            <div className="t-card__stars" aria-label="5 out of 5 stars">
                                {[0, 1, 2, 3, 4].map((i) => (
                                    <span key={i} className="t-card__star" aria-hidden="true">★</span>
                                ))}
                            </div>
                            <div className="t-card__divider" />
                            <div className="t-card__stats">
                                <div className="t-card__stat">
                                    <span className="t-card__stat-icon" aria-hidden="true"><Play size={16} strokeWidth={2.25} fill="currentColor" /></span>
                                    <span className="t-card__stat-copy">
                                        <strong>8x</strong>
                                        <span>more videos per month</span>
                                    </span>
                                </div>
                                <div className="t-card__stat">
                                    <span className="t-card__stat-icon" aria-hidden="true"><Clock size={16} strokeWidth={2.25} /></span>
                                    <span className="t-card__stat-copy">
                                        <strong>70%</strong>
                                        <span>less editing time</span>
                                    </span>
                                </div>
                            </div>
                            <div className="t-card__author">
                                <span className="t-card__avatar t-card__avatar--james" aria-hidden="true">JK</span>
                                <span className="t-card__author-meta">
                                    <span className="t-card__author-name">James K.</span>
                                    <span className="t-card__author-role">Startup Founder</span>
                                </span>
                            </div>
                        </article>

                        {/* Testimonial 3 */}
                        <article
                            ref={testimonialsColRightRef}
                            className="t-card landing-parallax-col"
                        >
                            <div className="t-card__quote-icon" aria-hidden="true">“</div>
                            <p className="t-card__quote">
                                “This is a honest review, the software is getting better month after month. Fantastic experience, the whole marketing team loves it.”
                            </p>
                            <div className="t-card__stars" aria-label="5 out of 5 stars">
                                {[0, 1, 2, 3, 4].map((i) => (
                                    <span key={i} className="t-card__star" aria-hidden="true">★</span>
                                ))}
                            </div>
                            <div className="t-card__divider" />
                            <div className="t-card__stats">
                                <div className="t-card__stat">
                                    <span className="t-card__stat-icon" aria-hidden="true"><Activity size={16} strokeWidth={2.25} /></span>
                                    <span className="t-card__stat-copy">
                                        <strong>4x</strong>
                                        <span>increase in reach</span>
                                    </span>
                                </div>
                                <div className="t-card__stat">
                                    <span className="t-card__stat-icon" aria-hidden="true"><Video size={16} strokeWidth={2.25} /></span>
                                    <span className="t-card__stat-copy">
                                        <strong>6x</strong>
                                        <span>more content output</span>
                                    </span>
                                </div>
                            </div>
                            <div className="t-card__author">
                                <span className="t-card__avatar t-card__avatar--maria" aria-hidden="true">ML</span>
                                <span className="t-card__author-meta">
                                    <span className="t-card__author-name">Maria L.</span>
                                    <span className="t-card__author-role">Marketing Director</span>
                                </span>
                            </div>
                        </article>
                    </div>

                    <p className="testimonials-section__trust">
                        <Heart size={14} strokeWidth={2} aria-hidden="true" />
                        Trusted by {/*1,000+ */}creators and teams worldwide.
                    </p>
                    
                    <div 
                        className="scroll-indicator" 
                        onClick={scrollToNext} 
                        style={{ marginTop: '40px' }}
                        role="button"
                        tabIndex={0}
                        aria-label="Scroll to next section"
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                scrollToNext();
                            }
                        }}
                    >
                        <ArrowDown size={32} aria-hidden="true" />
                    </div>
                </div>
            </section>

            {/* Pricing Section */}
            <section id="pricing-section" className="snap-section pricing-section reveal-on-scroll">
                <div className="section-content pricing-section__content">
                    <h2 className="pricing-section__title landing-section-title-solid">Pricing</h2>
                    <div className="pricing-section__intro">
                        <div className="pricing-pill">
                            <span className="pricing-pill__text">First 3 videos for free, no credit card required.</span>
                        </div>
                    </div>

                    <div className={`pricing-grid${isMobile ? ' pricing-grid--mobile' : ''}`}>
                        {/* Free Plan */}
                        <div className="p-card pricing-plan-card">
                            <h3 className="p-card__name">Free</h3>
                            <div className="p-card__price">
                                <span className="p-card__amount">{currency}0</span>
                                <span className="p-card__period">/month</span>
                            </div>
                            <div className="p-card__divider" />
                            <ul className="p-card__features">
                                <li>3 videos per month</li>
                                <li>Import raw content</li>
                                <li>Generate hooks, scripts, voiceovers</li>
                                <li>Generate videos</li>
                                <li>5GB storage</li>
                            </ul>
                            <button
                                type="button"
                                className="p-card__btn p-card__btn--outline"
                                onClick={handleGoogleSignup}
                                aria-label="Get started for free"
                            >
                                Get started for free
                            </button>
                        </div>

                        {/* Creator Plan */}
                        <div className="p-card p-card--popular pricing-plan-card">
                            <span className="p-card__badge">POPULAR</span>
                            <h3 className="p-card__name">Creator</h3>
                            <div className="p-card__price">
                                <span className="p-card__amount">{currency}25</span>
                                <span className="p-card__period">/month</span>
                            </div>
                            <div className="p-card__divider" />
                            <ul className="p-card__features">
                                <li><span className="p-card__em">20 videos</span> per month</li>
                                <li>Import raw content</li>
                                <li>Generate hooks, scripts, voiceovers</li>
                                <li>Generate videos</li>
                                <li><span className="p-card__em">50GB storage</span> (for raw videos)</li>
                            </ul>
                            <button
                                type="button"
                                className="p-card__btn p-card__btn--solid"
                                onClick={handleGoogleSignup}
                                aria-label="Start 3 videos for free"
                            >
                                Start 3 videos for free
                            </button>
                            <p className="p-card__note">No credit card required</p>
                        </div>

                        {/* Pro Plan */}
                        <div className="p-card pricing-plan-card">
                            <h3 className="p-card__name">Pro</h3>
                            <div className="p-card__price">
                                <span className="p-card__amount">{currency}99</span>
                                <span className="p-card__period">/month</span>
                            </div>
                            <div className="p-card__divider" />
                            <ul className="p-card__features">
                                <li><span className="p-card__em">150 videos</span> per month</li>
                                <li>Import raw content</li>
                                <li>Generate hooks, scripts, voiceovers</li>
                                <li>Generate videos</li>
                                <li><span className="p-card__em">200GB storage</span> per profile (for raw videos)</li>
                                <li><span className="p-card__em">Invite team members</span></li>
                                <li><span className="p-card__em">Multiple brand profiles (+{currency}99 per profile)</span></li>
                            </ul>
                            <div className="p-card__pro-banner">
                                <span className="p-card__infinity" aria-hidden="true">∞</span>
                                <span>Become an infinite content machine for {currency}99/month</span>
                            </div>
                        </div>
                    </div>

                    <p className="pricing-trust">
                        <span className="pricing-trust__icon" aria-hidden="true">
                            <Shield size={18} strokeWidth={2} />
                        </span>
                        Cancel anytime. No commitments.
                    </p>

                    <div 
                        className="scroll-indicator" 
                        onClick={scrollToNext}
                        role="button"
                        tabIndex={0}
                        aria-label="Scroll to next section"
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                scrollToNext();
                            }
                        }}
                    >
                        <ArrowDown size={32} aria-hidden="true" />
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="snap-section faq-section reveal-on-scroll">
                <div className="section-content">
                    <h2 className="section-title landing-section-title-solid" style={{marginBottom:'40px', paddingTop:'100px', fontSize:'32px', fontWeight: 700}}>FAQ</h2>
                    
                    <div
                        className="landing-faq"
                        style={{
                            ['--lf-rule']: darkMode ? 'rgba(255, 255, 255, 0.12)' : '#eeeeee',
                            ['--lf-q']: darkMode ? '#f2f2f2' : '#111111',
                            ['--lf-icon-bg']: darkMode ? 'rgba(255, 255, 255, 0.1)' : '#f2f2f2',
                            ['--lf-icon-fg']: darkMode ? '#fafafa' : '#111111',
                            ['--lf-answer']: darkMode ? 'rgba(220, 220, 220, 0.9)' : '#444444',
                        }}
                    >
                        {/* FAQ 1 */}
                        <div className="landing-faq__item">
                            <h3 className="landing-faq__heading">
                            <button
                                type="button"
                                className="landing-faq__trigger"
                                onClick={() => setOpenFAQ(openFAQ === 1 ? null : 1)}
                                aria-label={openFAQ === 1 ? "Collapse: How does Merra.ai work?" : "Expand: How does Merra.ai work?"}
                                aria-expanded={openFAQ === 1}
                            >
                                <span className="landing-faq__q">How does Merra.ai work?</span>
                                <span className="landing-faq__icon-wrap" aria-hidden="true">{openFAQ === 1 ? '−' : '+'}</span>
                            </button>
                            </h3>
                            {openFAQ === 1 && (
                                <div className="landing-faq__panel">
                                    <p className="landing-faq__answer">
                                        Simply upload your raw video content, and our AI automatically edits it for you. 
                                        No need for manual trimming, timeline editing, or technical skills. 
                                        You can regenerate as many times as you want until you're satisfied with the result.
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* FAQ 2 */}
                        <div className="landing-faq__item">
                            <h3 className="landing-faq__heading">
                            <button
                                type="button"
                                className="landing-faq__trigger"
                                onClick={() => setOpenFAQ(openFAQ === 2 ? null : 2)}
                                aria-label={openFAQ === 2 ? "Collapse: What types of videos can I create?" : "Expand: What types of videos can I create?"}
                                aria-expanded={openFAQ === 2}
                            >
                                <span className="landing-faq__q">What types of videos can I create?</span>
                                <span className="landing-faq__icon-wrap" aria-hidden="true">{openFAQ === 2 ? '−' : '+'}</span>
                            </button>
                            </h3>
                            {openFAQ === 2 && (
                                <div className="landing-faq__panel">
                                    <p className="landing-faq__answer">
                                        Merra.ai is perfect for short form social media content, and best for storytelling / voiceover content.
                                        Our goal is to create a script that is engaging and interesting to watch, then we edit the video with filler content.
                                        If you need content to exactly match the voiceover, for example a product demo, Merra AI won't be the best fit.
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* FAQ 3 */}
                        <div className="landing-faq__item">
                            <h3 className="landing-faq__heading">
                            <button
                                type="button"
                                className="landing-faq__trigger"
                                onClick={() => setOpenFAQ(openFAQ === 3 ? null : 3)}
                                aria-label={openFAQ === 3 ? "Collapse: Do I need video editing experience?" : "Expand: Do I need video editing experience?"}
                                aria-expanded={openFAQ === 3}
                            >
                                <span className="landing-faq__q">Do I need video editing experience?</span>
                                <span className="landing-faq__icon-wrap" aria-hidden="true">{openFAQ === 3 ? '−' : '+'}</span>
                            </button>
                            </h3>
                            {openFAQ === 3 && (
                                <div className="landing-faq__panel">
                                    <p className="landing-faq__answer">
                                        Not at all! Merra AI is designed for people who don't have time to edit videos 
                                        or don't have video editing skills. Our AI does all the heavy lifting, you just 
                                        upload and click 'regenerate' until you're happy.
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* FAQ 4 */}
                        <div className="landing-faq__item">
                            <h3 className="landing-faq__heading">
                            <button
                                type="button"
                                className="landing-faq__trigger"
                                onClick={() => setOpenFAQ(openFAQ === 4 ? null : 4)}
                                aria-label={openFAQ === 4 ? "Collapse: What if I'm not satisfied with the results?" : "Expand: What if I'm not satisfied with the results?"}
                                aria-expanded={openFAQ === 4}
                            >
                                <span className="landing-faq__q">What if I'm not satisfied with the results?</span>
                                <span className="landing-faq__icon-wrap" aria-hidden="true">{openFAQ === 4 ? '−' : '+'}</span>
                            </button>
                            </h3>
                            {openFAQ === 4 && (
                                <div className="landing-faq__panel">
                                    <p className="landing-faq__answer">
                                        You can regenerate unlimited times at no extra cost. Our AI learns your preferences 
                                        and each version gets better.
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* FAQ 5 */}
                        <div className="landing-faq__item">
                            <h3 className="landing-faq__heading">
                            <button
                                type="button"
                                className="landing-faq__trigger"
                                onClick={() => setOpenFAQ(openFAQ === 5 ? null : 5)}
                                aria-label={openFAQ === 5 ? "Collapse: Is my video content secure?" : "Expand: Is my video content secure?"}
                                aria-expanded={openFAQ === 5}
                            >
                                <span className="landing-faq__q">Is my video content secure?</span>
                                <span className="landing-faq__icon-wrap" aria-hidden="true">{openFAQ === 5 ? '−' : '+'}</span>
                            </button>
                            </h3>
                            {openFAQ === 5 && (
                                <div className="landing-faq__panel">
                                    <p className="landing-faq__answer">
                                        Absolutely. We take data security seriously. Your content is encrypted, stored securely, 
                                        and never shared with third parties. You can delete your content at any time.
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* FAQ 6 */}
                        <div className="landing-faq__item">
                            <h3 className="landing-faq__heading">
                            <button
                                type="button"
                                className="landing-faq__trigger"
                                onClick={() => setOpenFAQ(openFAQ === 6 ? null : 6)}
                                aria-label={openFAQ === 6 ? "Collapse: Will it look AI-generated?" : "Expand: Will it look AI-generated?"}
                                aria-expanded={openFAQ === 6}
                            >
                                <span className="landing-faq__q">Will it look AI-generated?</span>
                                <span className="landing-faq__icon-wrap" aria-hidden="true">{openFAQ === 6 ? '−' : '+'}</span>
                            </button>
                            </h3>
                            {openFAQ === 6 && (
                                <div className="landing-faq__panel">
                                    <p className="landing-faq__answer">
                                        No! Merra AI uses your own video content, so the final result looks completely authentic. 
                                        Our AI simply edits and enhances YOUR video, it doesn't generate artificial content. 
                                        You provide the footage, we provide the polish. The end result is professional-quality video 
                                        that showcases your brand authentically.
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* FAQ 7 */}
                        <div className="landing-faq__item">
                            <h3 className="landing-faq__heading">
                            <button
                                type="button"
                                className="landing-faq__trigger"
                                onClick={() => setOpenFAQ(openFAQ === 7 ? null : 7)}
                                aria-label={openFAQ === 7 ? "Collapse: Can I sign up for free?" : "Expand: Can I sign up for free?"}
                                aria-expanded={openFAQ === 7}
                            >
                                <span className="landing-faq__q">Can I sign up for free?</span>
                                <span className="landing-faq__icon-wrap" aria-hidden="true">{openFAQ === 7 ? '−' : '+'}</span>
                            </button>
                            </h3>
                            {openFAQ === 7 && (
                                <div className="landing-faq__panel">
                                    <p className="landing-faq__answer">
                                        Yes! Sign up is completely free with no credit card required. You can generate 2 videos, then you can upgrade to a paid plan. We offer flexible pricing plans 
                                        based on your needs. Check out the pricing section for more details. You can cancel at any time. Start your free trial today and see the difference Merra AI can make 
                                        for your content creation.
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* FAQ 8 */}
                        <div className="landing-faq__item">
                            <h3 className="landing-faq__heading">
                            <button
                                type="button"
                                className="landing-faq__trigger"
                                onClick={() => setOpenFAQ(openFAQ === 8 ? null : 8)}
                                aria-label={openFAQ === 8 ? "Collapse: How long does it take to create a video?" : "Expand: How long does it take to create a video?"}
                                aria-expanded={openFAQ === 8}
                            >
                                <span className="landing-faq__q">How long does it take to create a video?</span>
                                <span className="landing-faq__icon-wrap" aria-hidden="true">{openFAQ === 8 ? '−' : '+'}</span>
                            </button>
                            </h3>
                            {openFAQ === 8 && (
                                <div className="landing-faq__panel">
                                    <p className="landing-faq__answer">
                                        It usually takes 2 minutes to create a video.
                                    </p>
                                </div>
                            )}
                        </div>

                    </div>
                    <div 
                        className="scroll-indicator" 
                        onClick={scrollToNext}
                        role="button"
                        tabIndex={0}
                        aria-label="Scroll to next section"
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                scrollToNext();
                            }
                        }}
                    >
                        <ArrowDown size={32} aria-hidden="true" />
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="snap-section cta-section reveal-on-scroll"
             style={{background: 'linear-gradient(180deg,rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0) 100%)', marginBottom:'0px', paddingBottom:'0px', backgroundColor:isMobile ? 'white' : 'transparent'}}>
                <div className="section-content" style={{paddingBottom:'0px', marginBottom:'0px'}}>
                    <p className="section-text">
                        Still scrolling? <span style={{opacity: 0.6, fontStyle: 'italic'}}>Your audience should be scrolling your videos now.</span>
                    </p>
                    <h2 className="gradient-text section-title">
                        Join the movement
                    </h2>
                    <p className="section-text">
                        And become an infinite content machine.
                        <br/> <span style={{opacity: 0.6, fontStyle: 'italic'}}>Are you ready to see your brand everywhere?</span>
                    </p>
                    <div style={{
                        marginTop:'20px',
                        backgroundColor: 'var(--background-tertiary)',
                        borderBottom: '1px solid var(--border-primary)',
                        fontWeight: 'bold',
                        fontSize: '16px',
                        color: darkMode ? '#ff6b6b' : '#dc3545'
                    }}>
                        Limited early access{userCount != null ? `: ${userCount}/${EARLY_ACCESS_LIMIT}` : `${EARLY_ACCESS_LIMIT} users`}
                    </div>
                    <div className="cta-buttons" style={{marginTop:'0px'}}>
                        {/* <p style={{color: 'black'}}>
                            Use your google account to sign up
                            <br/>
                            <br/>
                            No credit card required.
                        </p> */}
                        <div style={{display:'flex', gap:'15px', alignItems:'center', justifyContent:'center', flexWrap:'wrap'}}>
                            <button 
                                onClick={handleGoogleSignup} 
                                className='google-btn' 
                                style={{flexShrink:1, maxWidth:'100%'}}
                                aria-label="Continue with Google to sign up"
                            >
                                <div className="google-icon"/>
                                Continue with Google
                            </button>
                        </div>
                        <div style={{display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center'}}>
                            <p
                                style={{
                                    wordBreak:'break-word',
                                    color: '#333', 
                                    margin: 0,
                                }}>
                                Make 3 videos for free • no credit card
                            </p>
                        </div>
                    </div>
                    <div style={{display:'flex', justifyContent:'center', gap:'20px', marginTop:'80px', flexWrap:'wrap', paddingBottom:'0px', marginBottom:'0px'}}>
                        <p style={{wordBreak:'break-word', margin: 0}}>
                            <Link 
                                to="/about" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                style={{ 
                                    color: '#333', 
                                    textDecoration: 'underline',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                }}
                            >
                                About
                                <ExternalLink size={14} />
                            </Link>
                        </p>
                        <p style={{wordBreak:'break-word', margin: 0}}>
                            <Link 
                                to="/blog" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                style={{ 
                                    color: '#333', 
                                    textDecoration: 'underline',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                }}
                            >
                                Blog
                                <ExternalLink size={14} />
                            </Link>
                        </p>
                        <p style={{wordBreak:'break-word', margin: 0}}>
                            <Link 
                                to="/blog/how-much-water-to-generate-one-video" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                style={{ 
                                    color: '#333', 
                                    textDecoration: 'underline',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                }}
                            >
                                🌱 How much water one video uses
                                <ExternalLink size={14} />
                            </Link>
                        </p>
                        <p style={{wordBreak:'break-word', margin: 0}}>
                            <Link 
                                to="/founder" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                style={{ 
                                    color: '#333', 
                                    textDecoration: 'underline',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                }}
                            >
                                Founder
                                <ExternalLink size={14} />
                            </Link>
                        </p>
                        <p style={{wordBreak:'break-word', margin: 0}}>
                            <Link 
                                to="/pricing" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                style={{ 
                                    color: '#333', 
                                    textDecoration: 'underline',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                }}
                            >
                                Pricing
                                <ExternalLink size={14} />
                            </Link>
                        </p>
                        <p style={{wordBreak:'break-word', margin: 0}}>
                            <Link 
                                to="/contact" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                style={{ 
                                    color: '#333', 
                                    textDecoration: 'underline',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                }}
                            >
                                Contact
                                <ExternalLink size={14} />
                            </Link>
                        </p>
                        <p style={{wordBreak:'break-word', margin: 0}}>
                            <Link 
                                to="/status" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                style={{ 
                                    color: '#333', 
                                    textDecoration: 'underline',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                }}
                            >
                                Status
                                <ExternalLink size={14} />
                            </Link>
                        </p>
                        <p style={{wordBreak:'break-word', margin: 0}}>
                            <Link 
                                to="/merra-or-manual" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                style={{ 
                                    color: '#333', 
                                    textDecoration: 'underline',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                }}
                            >
                                Merra or Manual ? The Big Question
                                <ExternalLink size={14} />
                            </Link>
                        </p>
                        <p style={{wordBreak:'break-word', margin: 0}}>
                            <Link 
                                to="/career" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                style={{ 
                                    color: '#333', 
                                    textDecoration: 'underline',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                }}
                            >
                                Career
                                <ExternalLink size={14} />
                            </Link>
                        </p>
                        <p style={{wordBreak:'break-word', margin: 0}}>
                            <Link 
                                to="/negotiation-brain-manifesto" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                style={{ 
                                    color: '#333', 
                                    textDecoration: 'underline',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                }}
                            >
                                The Negotiation Brain Manifesto
                                <ExternalLink size={14} />
                            </Link>
                        </p>
                        <p style={{wordBreak:'break-word', margin: 0}}>
                            <Link 
                                to="/merra-comparison" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                style={{ 
                                    color: '#333', 
                                    textDecoration: 'underline',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                }}
                            >
                                Comparison
                                <ExternalLink size={14} />
                            </Link>
                        </p>
                        <p style={{wordBreak:'break-word', margin: 0}}>
                            <Link 
                                to="/confidentiality-rules" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                style={{ 
                                    color: '#333', 
                                    textDecoration: 'underline',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                }}
                            >
                                Confidentiality Rules
                                <ExternalLink size={14} />
                            </Link>
                        </p>
                        <p style={{wordBreak:'break-word', margin: 0}}>
                            <Link 
                                to="/privacy-policy" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                style={{ 
                                    color: '#333', 
                                    textDecoration: 'underline',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                }}
                            >
                                Privacy Policy
                                <ExternalLink size={14} />
                            </Link>
                        </p>
                        <p style={{wordBreak:'break-word', margin: 0}}>
                            <Link 
                                to="/terms-of-service" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                style={{ 
                                    color: '#333', 
                                    textDecoration: 'underline',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                }}
                            >
                                Terms of Service
                                <ExternalLink size={14} />
                            </Link>
                        </p>
                        <p style={{wordBreak:'break-word', margin: 0}}>
                            <Link 
                                to="/media-kit" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                style={{ 
                                    color: '#333', 
                                    textDecoration: 'underline',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                }}
                            >
                                Press Kit & Media
                                <ExternalLink size={14} />
                            </Link>
                        </p>
                        <p style={{wordBreak:'break-word', margin: 0}}>
                            <Link 
                                to="/ambassador-programs" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                style={{ 
                                    color: '#333', 
                                    textDecoration: 'underline',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                }}
                            >
                                Make UGC content for Merra AI (and earn $$$)
                                <ExternalLink size={14} />
                            </Link>
                        </p>
                    </div>
                    {/* <div className="cta-buttons">
                        {isAuthenticated ? (
                            <button 
                                onClick={handleGoogleSignup} 
                                className="google-btn"
                                aria-label="Continue with Google to sign up"
                            >
                                <div className="google-icon" />
                                Continue with Google
                            </button>
                        ) : (
                            <>
                                <button 
                                    onClick={handleGoogleSignup} 
                                    className="google-btn"
                                    aria-label="Continue with Google to sign up"
                                >
                                    <div className="google-icon" />
                                    Continue with Google
                                </button>
                                <button onClick={handleRequestAccess} className="purple-button">
                                    <span>Request access</span>
                                    <span className="purple-button-shimmer"></span>
                                </button>
                                <button 
                                    onClick={() => window.open('https://calendly.com/boukar-o-sall/30min', '_blank')}
                                    className="purple-button"
                                    style={{ marginTop: '12px' }}
                                >
                                    <span>Book a 30-minute call with the founder</span>
                                    <span className="purple-button-shimmer"></span>
                                </button>
                            </>
                        )}
                    </div> */}
                </div>
            </section>

            {/* Comparison Link Section */}
            {/* <section className="snap-section comparison-section"
            style={{
                /*backgroundImage: 'url(/beach-best.png)',
                //backgroundSize: 'cover',
                //backgroundPosition: 'center',
                //backgroundRepeat: 'no-repeat',
                //position: 'relative'
                background: 'linear-gradient(180deg,rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0) 100%)',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',   // ⬅️ pushes buttons to bottom
                alignItems: 'center',
                minHeight: '85vh'                 // ⬅️ full section height
            }}>
                <div className="section-content" style={{display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex:1}}>
                    <div>
                        <h2 className="section-title landing-section-title-solid">
                            If it's not your vibe, here are tools you could use
                        </h2>
                        <Link to="/merra-comparison" className="comparison-link" target="_blank" rel="noopener noreferrer">
                            <span className="landing-section-title-solid" style={{textDecoration:'underline black'}}>Alternative options →</span>
                        </Link>
                    </div>
                </div>

                <div style={{display:'flex', justifyContent:'center', gap:'20px', marginTop:'40px', flexWrap:'wrap',}}>
                    <p style={{wordBreak:'break-word', margin: 0}}>
                        <Link 
                            to="/about" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{ 
                                color: '#333', 
                                textDecoration: 'underline',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px'
                            }}
                        >
                            About
                            <ExternalLink size={14} />
                        </Link>
                    </p>
                    <p style={{wordBreak:'break-word', margin: 0}}>
                        <Link 
                            to="/blog" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{ 
                                color: '#333', 
                                textDecoration: 'underline',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px'
                            }}
                        >
                            Blog
                            <ExternalLink size={14} />
                        </Link>
                    </p>
                    <p style={{wordBreak:'break-word', margin: 0}}>
                        <Link 
                            to="/blog/how-much-water-to-generate-one-video" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{ 
                                color: '#333', 
                                textDecoration: 'underline',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px'
                            }}
                        >
                            🌱 How much water one video uses
                            <ExternalLink size={14} />
                        </Link>
                    </p>
                    <p style={{wordBreak:'break-word', margin: 0}}>
                        <Link 
                            to="/founder" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{ 
                                color: '#333', 
                                textDecoration: 'underline',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px'
                            }}
                        >
                            Founder
                            <ExternalLink size={14} />
                        </Link>
                    </p>
                    <p style={{wordBreak:'break-word', margin: 0}}>
                        <Link 
                            to="/pricing" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{ 
                                color: '#333', 
                                textDecoration: 'underline',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px'
                            }}
                        >
                            Pricing
                            <ExternalLink size={14} />
                        </Link>
                    </p>
                    <p style={{wordBreak:'break-word', margin: 0}}>
                        <Link 
                            to="/contact" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{ 
                                color: '#333', 
                                textDecoration: 'underline',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px'
                            }}
                        >
                            Contact
                            <ExternalLink size={14} />
                        </Link>
                    </p>
                    <p style={{wordBreak:'break-word', margin: 0}}>
                        <Link 
                            to="/merra-or-manual" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{ 
                                color: '#333', 
                                textDecoration: 'underline',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px'
                            }}
                        >
                            Merra or Manual ? The Big Question
                            <ExternalLink size={14} />
                        </Link>
                    </p>
                    <p style={{wordBreak:'break-word', margin: 0}}>
                        <Link 
                            to="/career" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{ 
                                color: '#333', 
                                textDecoration: 'underline',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px'
                            }}
                        >
                            Career
                            <ExternalLink size={14} />
                        </Link>
                    </p>
                </div>
            </section> */}

            {/* Footer with Year Progress Bar */}
            <footer style={{
                padding: '20px',
                // borderTop: darkMode ? '1px solid #222529' : '1px solid #666',
                // backgroundColor: darkMode ? '#1a1b1e' : '#FCFCF8',
                backgroundColor: isMobile ? 'white' : 'transparent',
            }}>
                <div style={{
                    maxWidth: '1200px',
                    margin: '0 auto',
                }}>
                    {/* <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: '8px',
                        opacity: '0.8',
                        fontSize: '14px',
                        color: 'black'
                    }}>
                        <span>Year {new Date().getFullYear()} progression {isMobile ? '' : '(reminder to do the most)'}</span>
                        <span>{calculateYearProgress()}% complete</span>
                    </div> */}
                    {/* <div style={{
                        width: '100%',
                        height: '4px',
                        backgroundColor: darkMode ? '#2A2B2F' : '#214B23',
                        borderRadius: '2px',
                        overflow: 'hidden',
                        position: 'relative'
                    }}>
                        <div style={{
                            width: `${calculateYearProgress()}%`,
                            height: '100%',
                            backgroundColor: '#4CAF50',
                            borderRadius: '2px',
                            transition: 'width 0.3s ease'
                        }}/>
                    </div> */}
                    <p style={{
                        marginTop: '12px',
                        marginBottom: 0,
                        fontSize: '13px',
                        // color: darkMode ? '#888' : '#555',
                        color: '#333333',
                        textAlign: 'center',
                        lineHeight: 1.4,
                        maxWidth: '560px',
                        marginLeft: 'auto',
                        marginRight: 'auto'
                    }}>
                        Merra AI is an AI video editor for automatic video editing and batch video creation—create multiple videos from one recording with our voiceover video creator. Repurpose video content for social media in bulk.
                    </p>
                    {/* {parseFloat(calculateYearProgress()) > 50 && (
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            marginTop: '8px',
                            fontSize: '12px',
                            opacity: '1',
                            color: 'black'
                        }}>
                            <span style={{ color: '#4CAF50' }}>👆<span style={{opacity:'0.7'}}> experience</span></span>
                            <span>
                                {(() => {
                                    const daysRemaining = getDaysRemainingInYear();
                                    return daysRemaining < 50 ? `(${daysRemaining} days) ` : '';
                                })()}
                                <span style={{opacity:'0.7'}}>opportunity</span> ☝️
                            </span>
                        </div>
                    )} */}
                </div>
                <div style={{textAlign:'center', marginTop:'20px', color: 'black'}}>
                    {/* <p style={{fontSize:'18px', marginTop:'0px', color:'#EC6C5C', textAlign:'center'}}>100% free during Product Hunt launch weekend 🥳</p> */}
                    <p style={{fontSize:'18px', marginTop:'0px', textAlign:'center'}}>Made with ♥️ and 🍕</p>
                    <p style={{fontSize:'18px', marginTop:'0px', textAlign:'center'}}>© 2025 Merra.ai</p>
                    <div className="badges-marquee" aria-label="Merra featured badges">
                        <div className="badges-marquee__track">
                            <div className="badges-marquee__group">
                                <a href="https://findly.tools/merra-ai?utm_source=merra-ai" target="_blank" rel="noopener noreferrer" className="badges-marquee__item">
                                    <img src="https://findly.tools/badges/findly-tools-badge-light.svg" alt="Featured on findly.tools" width="150" />
                                </a>
                                <a href="https://startupfa.me/s/merraai?utm_source=merra.ai" target="_blank" rel="noopener noreferrer" className="badges-marquee__item">
                                    <img src="https://startupfa.me/badges/featured-badge.webp" alt="Merra.ai - Featured on Startup Fame" width="171" height="54" />
                                </a>
                                <a href="https://twelve.tools" target="_blank" rel="noopener noreferrer" className="badges-marquee__item">
                                    <img src="https://twelve.tools/badge0-white.svg" alt="Featured on Twelve Tools" width="200" height="54" />
                                </a>
                                <a href="https://wired.business" target="_blank" rel="noopener noreferrer" className="badges-marquee__item">
                                    <img src="https://wired.business/badge0-white.svg" alt="Featured on Wired Business" width="200" height="54" />
                                </a>
                                <a href="https://dang.ai/" target="_blank" rel="noopener noreferrer" className="badges-marquee__item">
                                    <img src="https://cdn.prod.website-files.com/63d8afd87da01fb58ea3fbcb/6487e2868c6c8f93b4828827_dang-badge.png" alt="Dang.ai" style={{ width: '150px', height: '54px' }} width="150" height="54" />
                                </a>
                                <a href="https://fazier.com/launches/merra.ai" target="_blank" rel="noopener noreferrer" className="badges-marquee__item">
                                    <img src="https://fazier.com/api/v1//public/badges/launch_badges.svg?badge_type=featured&theme=light" width="250" alt="Fazier badge" />
                                </a>
                                <a href="https://ufind.best/products/merra?utm_source=merra" target="_blank" rel="noopener noreferrer" className="badges-marquee__item">
                                    <img src="https://ufind.best/badges/ufind-best-badge-light.svg" alt="Featured on ufind.best" width="150" />
                                </a>
                                <a href="https://www.proofstories.io/directory/products/merra/" target="_blank" rel="noopener noreferrer" style={{ height: '60px' }} className="badges-marquee__item">
                                    <img src="https://www.proofstories.io/directory/badges/l/merra.svg" alt="Merra badge" style={{ height: '60px' }} />
                                </a>
                                <a href="https://indiehunt.io/project/merra-ai" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', display: 'inline-block' }} className="badges-marquee__item">
                                    <img src="https://indiehunt.io/badges/indiehunt-badge-light.svg" alt="Featured on IndieHunt" style={{ width: '265px', height: '58px', display: 'block' }} />
                                </a>
                                <a href="https://earlyhunt.com" target="_blank" rel="noopener noreferrer" className="badges-marquee__item">
                                    <img src="https://earlyhunt.com/badges/earlyhunt-badge-light.svg" alt="Featured on EarlyHunt" width="265" height="58" />
                                </a>
                                <a href="https://launchigniter.com/product/merra?ref=badge-merra" target="_blank" rel="noopener noreferrer" className="badges-marquee__item">
                                    <img src="https://launchigniter.com/api/badge/merra?theme=light" alt="Featured on LaunchIgniter" width="212" height="55" />
                                </a>
                                <a href="https://aiagentsdirectory.com/agent/merra?utm_source=badge&utm_medium=referral&utm_campaign=free_listing&utm_content=merra" target="_blank" rel="noopener noreferrer" className="badges-marquee__item">
                                    <img src="https://aiagentsdirectory.com/featured-badge.svg?v=2024" alt="Merra - Featured AI Agent on AI Agents Directory" width="200" height="50" />
                                </a>
                                <a href="https://startupslab.site" target="_blank" rel="noopener noreferrer" className="badges-marquee__item">
                                    <img src="https://cdn.startupslab.site/site-images/badge-light.png" alt="Featured on Startups Lab" width="200" height="54" />
                                </a>
                                <a href="https://www.techtrendin.com/products/merra" target="_blank" rel="noopener noreferrer" className="badges-marquee__item">
                                    <img src="https://www.techtrendin.com/badges/featured-light.png" alt="Featured on TechTrendin'" style={{ width: 'auto', height: '52px' }} />
                                </a>
                                <a href="https://marketingdb.live" target="_blank" rel="noopener noreferrer" className="badges-marquee__item">
                                    <img src="https://marketingdb.live/badge.svg" alt="Listed on MarketingDB" width="190" height="44" />
                                </a>
                            </div>
                            <div className="badges-marquee__group" aria-hidden="true">
                                <a href="https://findly.tools/merra-ai?utm_source=merra-ai" target="_blank" rel="noopener noreferrer" className="badges-marquee__item">
                                    <img src="https://findly.tools/badges/findly-tools-badge-light.svg" alt="Featured on findly.tools" width="150" />
                                </a>
                                <a href="https://startupfa.me/s/merraai?utm_source=merra.ai" target="_blank" rel="noopener noreferrer" className="badges-marquee__item">
                                    <img src="https://startupfa.me/badges/featured-badge.webp" alt="Merra.ai - Featured on Startup Fame" width="171" height="54" />
                                </a>
                                <a href="https://twelve.tools" target="_blank" rel="noopener noreferrer" className="badges-marquee__item">
                                    <img src="https://twelve.tools/badge0-white.svg" alt="Featured on Twelve Tools" width="200" height="54" />
                                </a>
                                <a href="https://wired.business" target="_blank" rel="noopener noreferrer" className="badges-marquee__item">
                                    <img src="https://wired.business/badge0-white.svg" alt="Featured on Wired Business" width="200" height="54" />
                                </a>
                                <a href="https://dang.ai/" target="_blank" rel="noopener noreferrer" className="badges-marquee__item">
                                    <img src="https://cdn.prod.website-files.com/63d8afd87da01fb58ea3fbcb/6487e2868c6c8f93b4828827_dang-badge.png" alt="Dang.ai" style={{ width: '150px', height: '54px' }} width="150" height="54" />
                                </a>
                                <a href="https://fazier.com/launches/merra.ai" target="_blank" rel="noopener noreferrer" className="badges-marquee__item">
                                    <img src="https://fazier.com/api/v1//public/badges/launch_badges.svg?badge_type=featured&theme=light" width="250" alt="Fazier badge" />
                                </a>
                                <a href="https://ufind.best/products/merra?utm_source=merra" target="_blank" rel="noopener noreferrer" className="badges-marquee__item">
                                    <img src="https://ufind.best/badges/ufind-best-badge-light.svg" alt="Featured on ufind.best" width="150" />
                                </a>
                                <a href="https://www.proofstories.io/directory/products/merra/" target="_blank" rel="noopener noreferrer" style={{ height: '60px' }} className="badges-marquee__item">
                                    <img src="https://www.proofstories.io/directory/badges/l/merra.svg" alt="Merra badge" style={{ height: '60px' }} />
                                </a>
                                <a href="https://indiehunt.io/project/merra-ai" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', display: 'inline-block' }} className="badges-marquee__item">
                                    <img src="https://indiehunt.io/badges/indiehunt-badge-light.svg" alt="Featured on IndieHunt" style={{ width: '265px', height: '58px', display: 'block' }} />
                                </a>
                                <a href="https://earlyhunt.com" target="_blank" rel="noopener noreferrer" className="badges-marquee__item">
                                    <img src="https://earlyhunt.com/badges/earlyhunt-badge-light.svg" alt="Featured on EarlyHunt" width="265" height="58" />
                                </a>
                                <a href="https://launchigniter.com/product/merra?ref=badge-merra" target="_blank" rel="noopener noreferrer" className="badges-marquee__item">
                                    <img src="https://launchigniter.com/api/badge/merra?theme=light" alt="Featured on LaunchIgniter" width="212" height="55" />
                                </a>
                                <a href="https://aiagentsdirectory.com/agent/merra?utm_source=badge&utm_medium=referral&utm_campaign=free_listing&utm_content=merra" target="_blank" rel="noopener noreferrer" className="badges-marquee__item">
                                    <img src="https://aiagentsdirectory.com/featured-badge.svg?v=2024" alt="Merra - Featured AI Agent on AI Agents Directory" width="200" height="50" />
                                </a>
                                <a href="https://startupslab.site" target="_blank" rel="noopener noreferrer" className="badges-marquee__item">
                                    <img src="https://cdn.startupslab.site/site-images/badge-light.png" alt="Featured on Startups Lab" width="200" height="54" />
                                </a>
                                <a href="https://www.techtrendin.com/products/merra" target="_blank" rel="noopener noreferrer" className="badges-marquee__item">
                                    <img src="https://www.techtrendin.com/badges/featured-light.png" alt="Featured on TechTrendin'" style={{ width: 'auto', height: '52px' }} />
                                </a>
                                <a href="https://marketingdb.live" target="_blank" rel="noopener noreferrer" className="badges-marquee__item">
                                    <img src="https://marketingdb.live/badge.svg" alt="Listed on MarketingDB" width="190" height="44" />
                                </a>
                            </div>
                        </div>
                    </div>
                    <p style={{fontSize:'18px', marginTop:'10px', textAlign:'center', marginBottom:'0px'}}>You found the team 👋</p>
                </div>
                <div style={{display:'flex', justifyContent:'center', alignItems:'center', marginTop:'0px', paddingTop:'0px'}}>
                    {TEAM_MEMBERS.map((member, index) => (
                        <TeamMemoji
                            key={member.name}
                            member={member}
                            index={index}
                            isMobile={isMobile}
                        />
                    ))}
                </div>
            </footer>
            {!isMobile && <img loading="lazy" src="/beach-best.webp" alt="Beach" style={{position:'absolute', bottom:0, left:0, width:'100%', height:'200vh', objectFit:'cover', zIndex:-1, backgroundColor:'pink'}} />}
            {!isMobile && <div style={{width:'100vw', height:'200vh', backgroundColor:'white', position:'absolute', bottom:0, left:0, zIndex:-2}} />}

            {/* Waitlist Modal */}
            {showWaitlistModal && (
                <div
                    onClick={() => setShowWaitlistModal(false)}
                    className="modal-overlay"
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="modal-content"
                    >
                        <button
                            onClick={() => setShowWaitlistModal(false)}
                            className="modal-close"
                        >
                            ×
                        </button>
                        <h2 className="modal-title" style={{fontWeight: 700}}>
                            Get early access to Merra.ai
                        </h2>
                        <div className="field-wrapper">
                            <input
                                value={waitlistEmail}
                                onChange={(e) => setWaitlistEmail(e.target.value)}
                                placeholder="Your email"
                                type="email"
                                className="field"
                                required
                            />
                            <button
                                onClick={handleWaitlistSignup}
                                className="purple-button"
                            >
                                <span>Join waitlist</span>
                                <span className="purple-button-shimmer"></span>
                            </button>
                            {waitlistMessage && (
                                <p className={`waitlist-message ${waitlistMessage.includes('Thank you') ? 'success' : 'error'}`}>
                                    {waitlistMessage}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default LandingPage1;

